import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Paper,
  Button,
  Portal,
  Radio,
  RadioGroup,
  Select,
  MenuItem,
  FormHelperText,
  CircularProgress,
  Alert,
  alpha,
  TextField,
  Chip,
  Divider,
  IconButton,
  useMediaQuery,
} from '@mui/material';
import { ChevronLeft as ChevronLeftIcon, ChevronRight as ChevronRightIcon } from '@mui/icons-material';
import ScrollableDialog from '../../components/common/ScrollableDialog';
import StripeEmbeddedCheckoutDialog from '../../components/common/StripeEmbeddedCheckoutDialog';
import PrimaryActionButton from '../../components/common/PrimaryActionButton';
import FolderTabs from '../../components/common/FolderTabs';
import BookingWaitlistCta from '../../components/booking/BookingWaitlistCta';
import BookingWaitlistDialog from '../../components/booking/BookingWaitlistDialog';
import BookingTimeSlotCoins from '../../components/booking/BookingTimeSlotCoins';
import BookingPaymentTerms, { resolveActivePaymentOption } from '../../components/booking/BookingPaymentTerms';
import { getServiceName } from '../../utils/serviceTranslations';
import { formatCurrency } from '../../utils/currencyUtils';
import { calculateBookingReviewAmountDue } from '../../utils/bookingAmountDueUtils';
import { getProductLinesTotal } from '../../components/products/ProductSaleLinesField';
import BookingAddProductsDialog from '../../components/booking/BookingAddProductsDialog';
import { formatProductLinesSummary } from '../../utils/productSaleItemsUtils';
import ProductService from '../../services/productService';
import requestDeduplicator from '../../utils/requestDeduplicator';
import { filterAgendaVisibleTeamMembers } from '../../utils/teamMemberBookingUtils';
import { filterBookingSlotsForPlan, isPublicFreePlan, shouldSkipProfessionalsStep } from '../../utils/storePlan';
import { useSupportChat } from '../../context/SupportChatContext';
import {
  formatServiceDurationLabel,
  formatServicePriceLabel,
  hasCustomerBookingDuration,
  selectCustomerBookableServices,
  usesCatalogPrice,
  withCustomerBookingDuration,
} from '../../utils/serviceValueFormat';
import StoreService from '../../services/storeService';
import ServiceComboService, { mergeCatalogCombos } from '../../services/serviceComboService';
import { isCustomerBookableService } from '../../utils/freeServiceUtils';
import api from '../../services/api';
import {
  getAvailability,
  createAppointment,
  createAppointmentCheckoutSession,
  abandonAppointmentCheckout,
  getAppointmentCheckoutOutcome,
  getPrepaymentQuote,
  toPolicyLanguage,
} from '../../services/appointmentService';
import {
  getBookingMembershipEntitlements,
  getStoreMembershipPlans,
  getCustomerMemberships,
} from '../../services/customerMembershipService';
import {
  getBookingServicePackageEntitlements,
  getCustomerServicePackages,
} from '../../services/customerServicePackageService';
import { isCustomerServicePackageUsable } from '../../services/servicePackageService';
import ServiceCategoryService from '../../services/serviceCategoryService';
import { formatCalendarDate } from '../../utils/dateFormatUtils';
import { getLocalizedValue } from '../../constants/Languages';
import { useStore } from '../../context/StoreContext';
import { buildCustomerRoute } from '../../utils/storeUtils';
import { normalizeLanguage, DEFAULT_LANGUAGE } from '../../constants/Languages';
import {
  allowsInStore,
  allowsOnline,
  normalizeCustomerPaymentMode,
} from '../../constants/CustomerPaymentMode';
import {
  computeSuggestedAssignment,
  formatMinutesToTime,
  getTeamMembersWhoCanDoAllServices,
  getTeamMembersWhoCanDoService,
  memberCanPerformBookingService,
  isNonStandaloneSecondaryBookingService,
  parseDurationToMinutes,
  parseTimeToMinutes,
  buildSequentialAssignments,
  buildOpenProfessionalAssignments,
  getBookingServicePriority,
} from '../../utils/appointmentBookingUtils';
import {
  filterTeamMembersByPlan,
  getActiveMembershipPlans,
  getSelectedMembershipPeriodEndUtc,
  isMembershipRenewalRisk,
} from '../../utils/membershipBookingUtils';
import { matchServiceCombos } from '../../utils/serviceComboMatch';
import { getApiErrorCode } from '../../utils/apiErrorCode';
import {
  buildCustomerBookingRequest,
  customerBookingPaysOnline,
  resolveBookingConfirmResult,
  resolveBookingPaymentOutcome,
} from '../../utils/customerBookingRequest';
import useCustomerOnlinePayments from '../../hooks/useCustomerOnlinePayments';
import useConsentGate from '../../hooks/useConsentGate';
import { ConsentContextType } from '../../services/consentFormService';
import { isStoreClosedOnDate } from '../../utils/waitlistWindowUtils';
import { BRAND, brandAlpha } from '../../theme/brandTokens';

const STEP_SERVICES = 0;
const STEP_PROFESSIONALS = 1;
const STEP_CALENDAR = 2;
const STEP_REVIEW = 3;
const ALL_SERVICES_TAB = 'all';
const COMBOS_TAB = 'combos';

const BOOKING_ERROR_KEYS = {
  CancellationPolicyNotAccepted: 'customer.appointments.booking.policy.acceptRequired',
  AppointmentPrepaymentRequired: 'customer.appointments.booking.policy.prepaymentRequired',
  MembershipEntitlementRequiresAllServices: 'customer.appointments.booking.entitlementErrors.membershipRequiresAllServices',
  MembershipEntitlementExceeded: 'customer.appointments.booking.entitlementErrors.membershipExceeded',
  ServicePackageEntitlementNotApplicable: 'customer.appointments.booking.entitlementErrors.packageRequiresAllServices',
  ServicePackageEntitlementExhausted: 'customer.appointments.booking.entitlementErrors.packageExhausted',
  ServicePackageExpired: 'customer.appointments.booking.entitlementErrors.packageExpired',
  ServicePackageTeamMemberNotAllowed: 'customer.appointments.booking.entitlementErrors.packageProfessional',
  CustomerServicePackageNotEligible: 'customer.appointments.booking.entitlementErrors.packageNotUsable',
  AppointmentEntitlementConflict: 'customer.appointments.booking.entitlementErrors.conflict',
  ConsentFormRequired: 'consentForms.errors.required',
  FreeServiceUsageLimitExceeded: 'customer.appointments.booking.freeServiceUsageExceeded',
  ServiceNotVisibleToCustomers: 'customer.appointments.booking.serviceNotVisible',
};

const PACKAGE_PAYMENT_PREFIX = 'package:';

const hoverCapableStyles = (hoverStyles) => ({
  '@media (hover: hover) and (pointer: fine)': {
    '&:hover': hoverStyles,
  },
});

export default function BookingPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const { storeId, storeOverview } = useStore();
  const { openChat } = useSupportChat();
  const { ensureConsents, consentDialog } = useConsentGate(storeId);
  const isFreePlan = isPublicFreePlan(storeOverview);
  const authorizationHoursForPlan = useMemo(
    () => (isFreePlan ? [] : (storeOverview?.authorizationHours || [])),
    [isFreePlan, storeOverview?.authorizationHours],
  );
  const lang = normalizeLanguage(i18n.language) || DEFAULT_LANGUAGE;
  const storeLanguage = normalizeLanguage(storeOverview?.storeLanguage) || DEFAULT_LANGUAGE;
  const currencyCode = storeOverview?.currency || 'EUR';
  const translateServiceName = (nameDictionary) => getServiceName(nameDictionary, lang, storeLanguage);
  const formatCustomerPrice = (price) => (
    Number(price) === 0 ? t('customer.store.freePrice') : formatCurrency(price, currencyCode)
  );
  const isMobileViewport = useMediaQuery((theme) => theme.breakpoints.down('sm'));

  const [services, setServices] = useState(location.state?.services ?? null);
  const [serviceCategories, setServiceCategories] = useState([]);
  const [serviceCategoryTab, setServiceCategoryTab] = useState(ALL_SERVICES_TAB);
  const [teamMembers, setTeamMembers] = useState(location.state?.teamMembers ?? null);
  const [loadingData, setLoadingData] = useState(!location.state?.services && !location.state?.teamMembers);
  const [activeStep, setActiveStep] = useState(
    () => (location.state?.preSelectedMembershipPlanId ? STEP_PROFESSIONALS : STEP_SERVICES)
  );
  const [selectedServices, setSelectedServices] = useState(() => {
    const preIds = location.state?.preSelectedServiceIds;
    const stateServices = location.state?.services;
    if (preIds && stateServices) {
      return selectCustomerBookableServices(stateServices, preIds);
    }
    return [];
  });
  const [professionalMode, setProfessionalMode] = useState('any');
  const [specificTeamMemberId, setSpecificTeamMemberId] = useState('');
  const [perServiceAssignments, setPerServiceAssignments] = useState({});
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTimeSlot, setSelectedTimeSlot] = useState(null);
  const [selectedTeamMemberIdForSlot, setSelectedTeamMemberIdForSlot] = useState(null);
  const [availabilityByDate, setAvailabilityByDate] = useState({});
  const [groupAvailabilityByDate, setGroupAvailabilityByDate] = useState({});
  const [hasDifferentialPricing, setHasDifferentialPricing] = useState(false);
  const [selectedGroupTimeSlots, setSelectedGroupTimeSlots] = useState({});
  const [loadingAvailability, setLoadingAvailability] = useState(false);
  const [detailsFromCustomer, setDetailsFromCustomer] = useState('');
  const [catalogProducts, setCatalogProducts] = useState([]);
  const [productLines, setProductLines] = useState(() => (
    (location.state?.preSelectedProductIds || []).map((productId) => ({ productId, quantity: 1 }))
  ));
  const [productDialogOpen, setProductDialogOpen] = useState(false);
  const [pickerModalOpen, setPickerModalOpen] = useState(false);
  const [pickerSlot, setPickerSlot] = useState(null);
  const [pickerOptions, setPickerOptions] = useState([]);
  const [incompatibleProfessionalDialog, setIncompatibleProfessionalDialog] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState('inStore');
  const [checkoutAppointmentId, setCheckoutAppointmentId] = useState(null);
  const checkoutFinishedRef = useRef(false);
  const checkoutSessionRef = useRef(null);
  const [releasingCheckout, setReleasingCheckout] = useState(false);
  const [prepaymentQuote, setPrepaymentQuote] = useState(null);
  const [loadingPrepaymentQuote, setLoadingPrepaymentQuote] = useState(false);
  const [prepaymentQuoteFailed, setPrepaymentQuoteFailed] = useState(false);
  const [payInFull, setPayInFull] = useState(false);
  const [policyAccepted, setPolicyAccepted] = useState(false);
  const [bookingEntitlements, setBookingEntitlements] = useState([]);
  const [loadingBookingEntitlements, setLoadingBookingEntitlements] = useState(false);
  const [membershipPlans, setMembershipPlans] = useState([]);
  const [customerMemberships, setCustomerMemberships] = useState([]);
  const [selectedMembershipPlanId, setSelectedMembershipPlanId] = useState(null);
  const [customerServicePackages, setCustomerServicePackages] = useState([]);
  const [selectedServicePackageId, setSelectedServicePackageId] = useState(null);
  const [packageEntitlements, setPackageEntitlements] = useState([]);
  const [renewalWarningOpen, setRenewalWarningOpen] = useState(false);
  const [renewalContinueAction, setRenewalContinueAction] = useState('review');
  const [renewalPeriodEndDisplay, setRenewalPeriodEndDisplay] = useState('');
  const [stripPage, setStripPage] = useState(0);
  const [datesRowWidth, setDatesRowWidth] = useState(0);
  const [waitlistOpen, setWaitlistOpen] = useState(false);
  const dateStripRowRef = useRef(null);
  const loadedDatesRef = useRef(new Set());
  const preSelectionAppliedRef = useRef(false);
  const membershipPlanPreselectionAppliedRef = useRef(false);
  const servicePackagePreselectionAppliedRef = useRef(false);
  const renewalAcknowledgedRef = useRef(false);

  const orderedSelectedServices = useMemo(
    () => selectedServices
      .map((service, index) => ({ service, index }))
      .sort((left, right) => (
        getBookingServicePriority(left.service) - getBookingServicePriority(right.service)
      ) || (left.index - right.index))
      .map(({ service }) => withCustomerBookingDuration(service)),
    [selectedServices]
  );
  const selectedServiceIds = useMemo(() => orderedSelectedServices.map((service) => service.id), [orderedSelectedServices]);
  const selectedCatalogServiceIds = useMemo(
    () => orderedSelectedServices.filter((service) => !service.isCombo).map((service) => service.id),
    [orderedSelectedServices],
  );
  const selectedComboIds = useMemo(
    () => orderedSelectedServices.filter((service) => service.isCombo).map((service) => service.id),
    [orderedSelectedServices],
  );
  const activeServices = useMemo(
    () => (services || []).filter((service) => isCustomerBookableService(service)),
    [services]
  );
  const visibleCategoryTabs = useMemo(
    () => serviceCategories.filter((category) => (
      category.isActive !== false
      && (category.serviceIds || []).some((serviceId) => activeServices.some((service) => service.id === serviceId))
    )),
    [activeServices, serviceCategories]
  );
  const catalogServices = useMemo(
    () => activeServices.filter((service) => !service.isCombo),
    [activeServices],
  );
  const comboServices = useMemo(
    () => activeServices.filter((service) => service.isCombo),
    [activeServices],
  );
  const hasCombos = comboServices.length > 0;
  const showServiceCategories = visibleCategoryTabs.length > 0 || hasCombos;
  const bookingCategoryTabs = useMemo(() => ([
    { value: ALL_SERVICES_TAB, label: t('customer.appointments.booking.categoryAll') },
    ...visibleCategoryTabs.map((category) => ({
      value: category.id,
      label: getLocalizedValue(category.name, lang, storeLanguage, t('serviceCategoriesManagement.card.unnamed')),
    })),
    ...(hasCombos ? [{ value: COMBOS_TAB, label: t('customer.appointments.booking.categoryCombos') }] : []),
  ]), [hasCombos, lang, storeLanguage, t, visibleCategoryTabs]);
  const displayedServices = useMemo(() => {
    if (serviceCategoryTab === COMBOS_TAB) {
      return comboServices;
    }

    if (serviceCategoryTab === ALL_SERVICES_TAB) {
      return catalogServices;
    }

    const category = visibleCategoryTabs.find((item) => item.id === serviceCategoryTab);
    if (!category) {
      return catalogServices;
    }

    const serviceIdSet = new Set(category.serviceIds || []);
    return catalogServices.filter((service) => serviceIdSet.has(service.id));
  }, [catalogServices, comboServices, serviceCategoryTab, visibleCategoryTabs]);
  // Memberships and packages are all-or-nothing: they only show up once every service they
  // include is part of the booking, and only when there is a use left.
  const eligibleMembershipEntitlements = useMemo(
    () => (bookingEntitlements || []).filter((entitlement) => entitlement.canRedeem),
    [bookingEntitlements]
  );
  const eligiblePackageEntitlements = useMemo(
    () => (packageEntitlements || []).filter((entitlement) => entitlement.canRedeem),
    [packageEntitlements]
  );
  const selectedMembershipId = selectedPaymentMethod.startsWith('membership:')
    ? selectedPaymentMethod.slice('membership:'.length)
    : null;
  const selectedPackagePaymentId = selectedPaymentMethod.startsWith(PACKAGE_PAYMENT_PREFIX)
    ? selectedPaymentMethod.slice(PACKAGE_PAYMENT_PREFIX.length)
    : null;
  const appointmentPaymentMode = normalizeCustomerPaymentMode(storeOverview?.appointmentCustomerPaymentMode);
  // The store can take this customer's payments in store only: then there is no online option
  // at all, even at a store that is otherwise online-only, and deposits are not asked for.
  const customerOnlinePaymentsAllowed = useCustomerOnlinePayments(storeId);
  const hasUnpricedSelection = orderedSelectedServices.some((service) => !usesCatalogPrice(service));
  const appointmentAllowsOnline = customerOnlinePaymentsAllowed
    && allowsOnline(appointmentPaymentMode)
    && !hasUnpricedSelection;
  // A service that asks for a deposit or prepayment takes "pay in store" off the table.
  const prepaymentRequired = customerOnlinePaymentsAllowed && Boolean(prepaymentQuote?.requiresPrepayment);
  const appointmentAllowsInStore = (!customerOnlinePaymentsAllowed || allowsInStore(appointmentPaymentMode))
    && !prepaymentRequired;
  const showAppointmentPaymentChoice = eligibleMembershipEntitlements.length > 0
    || eligiblePackageEntitlements.length > 0
    || (appointmentAllowsOnline && appointmentAllowsInStore);
  const hasCompanionEligibleService = useMemo(
    () => orderedSelectedServices.some((service) => getBookingServicePriority(service) < 2),
    [orderedSelectedServices]
  );
  const hasInvalidSecondarySelection = useMemo(
    () => orderedSelectedServices.some((service) => isNonStandaloneSecondaryBookingService(service)) && !hasCompanionEligibleService,
    [hasCompanionEligibleService, orderedSelectedServices]
  );
  const ACTIVE_BOOKING_MEMBERSHIP_STATUSES = new Set(['Active', 'Trialing']);
  const activeMembershipPlans = useMemo(
    () => getActiveMembershipPlans(membershipPlans, customerMemberships),
    [membershipPlans, customerMemberships]
  );
  const showMembershipDiscovery = Boolean(storeOverview?.hasActiveMembershipPlans)
    && activeMembershipPlans.length === 0
    && !selectedMembershipPlanId;
  const selectedPlan = useMemo(
    () => membershipPlans.find((p) => p.id === selectedMembershipPlanId) ?? null,
    [membershipPlans, selectedMembershipPlanId]
  );
  const bookingPlanCards = useMemo(() => {
    if (!selectedPlan) return activeMembershipPlans;
    if (activeMembershipPlans.some((plan) => plan.id === selectedPlan.id)) return activeMembershipPlans;
    return [selectedPlan];
  }, [selectedPlan, activeMembershipPlans]);
  const selectedPlanMembership = useMemo(() => {
    if (!selectedMembershipPlanId) return null;
    return customerMemberships.find(
      (m) => m.membershipPlanId === selectedMembershipPlanId && ACTIVE_BOOKING_MEMBERSHIP_STATUSES.has(m.status)
    ) ?? null;
  }, [selectedMembershipPlanId, customerMemberships]); // eslint-disable-line react-hooks/exhaustive-deps
  const usableServicePackages = useMemo(
    () => customerServicePackages.filter(isCustomerServicePackageUsable),
    [customerServicePackages]
  );
  const selectedServicePackage = useMemo(
    () => usableServicePackages.find((servicePackage) => servicePackage.id === selectedServicePackageId) ?? null,
    [usableServicePackages, selectedServicePackageId]
  );
  const showPackageDiscovery = Boolean(storeOverview?.hasActiveServicePackages)
    && usableServicePackages.length === 0
    && !showMembershipDiscovery;
  // The membership plan or package the customer chose to book with: its services stay in the
  // booking and only its professionals are offered.
  const entitlementLock = useMemo(() => {
    if (selectedPlan) {
      return { serviceIds: selectedPlan.serviceIds || [], teamMemberIds: selectedPlan.teamMemberIds || [] };
    }
    if (selectedServicePackage) {
      return {
        serviceIds: selectedServicePackage.includedServiceIds,
        teamMemberIds: selectedServicePackage.allowedTeamMemberIds,
      };
    }
    return null;
  }, [selectedPlan, selectedServicePackage]);
  const planTeamMemberIdSet = useMemo(
    () => (entitlementLock?.teamMemberIds?.length > 0 ? new Set(entitlementLock.teamMemberIds) : null),
    [entitlementLock]
  );

  const eligibleTeamMembers = useMemo(
    () => {
      const canDoSelected = (teamMembers || []).filter((member) => (
        orderedSelectedServices.some((service) => memberCanPerformBookingService(member, service))
      ));
      if (!planTeamMemberIdSet) return canDoSelected;
      return canDoSelected.filter((m) => planTeamMemberIdSet.has(m.id));
    },
    [teamMembers, orderedSelectedServices, planTeamMemberIdSet]
  );
  const servicesById = useMemo(
    () => new Map(activeServices.map((service) => [service.id, service])),
    [activeServices],
  );
  const canDoAll = useMemo(
    () => getTeamMembersWhoCanDoAllServices(eligibleTeamMembers, selectedServiceIds, servicesById),
    [eligibleTeamMembers, selectedServiceIds, servicesById]
  );
  const suggestedAssignment = useMemo(
    () => computeSuggestedAssignment(eligibleTeamMembers, selectedServiceIds, servicesById),
    [eligibleTeamMembers, selectedServiceIds, servicesById]
  );
  const specificOptionDisabled = selectedServiceIds.length > 0 && canDoAll.length === 0;
  const skipProfessionalsStep = selectedServiceIds.length > 0 && shouldSkipProfessionalsStep(canDoAll);

  const goToProfessionalsOrCalendar = () => {
    if (skipProfessionalsStep && canDoAll[0]) {
      setProfessionalMode('specific');
      setSpecificTeamMemberId(canDoAll[0].id);
      setActiveStep(STEP_CALENDAR);
      return;
    }
    setActiveStep(STEP_PROFESSIONALS);
  };
  const professionalScheduleGroups = useMemo(() => {
    if (professionalMode !== 'perService') {
      return [];
    }

    const groups = new Map();

    orderedSelectedServices.forEach((service) => {
      const teamMemberId = perServiceAssignments[service.id];
      if (!teamMemberId) {
        return;
      }

      if (!groups.has(teamMemberId)) {
        groups.set(teamMemberId, {
          cacheKey: teamMemberId,
          teamMemberId,
          teamMemberName: teamMembers?.find((teamMember) => teamMember.id === teamMemberId)?.name || '',
          services: [],
        });
      }

      groups.get(teamMemberId).services.push(service);
    });

    return Array.from(groups.values());
  }, [orderedSelectedServices, professionalMode, perServiceAssignments, teamMembers]);
  const showGroupedProfessionalSchedules = professionalMode === 'perService';
  const professionalScheduleColumns = useMemo(() => professionalScheduleGroups.map((group, index) => {
    const slotTimes = selectedDate
      ? (groupAvailabilityByDate[group.cacheKey]?.[selectedDate] || []).map((slot) => (typeof slot.timeSlot === 'string' ? slot.timeSlot : String(slot.timeSlot ?? '')))
      : [];

    return {
      ...group,
      columnIndex: index,
      durationMinutes: group.services.reduce((total, service) => total + parseDurationToMinutes(service.duration), 0),
      serviceSummary: group.services.map((service) => getServiceName(service.name, lang, storeLanguage)).join(' + '),
      slotTimes,
      slotTimesSet: new Set(slotTimes),
    };
  }), [groupAvailabilityByDate, professionalScheduleGroups, selectedDate, lang, storeLanguage]);
  const groupedTimelineTimeSlots = useMemo(() => {
    const allSlotMinutes = professionalScheduleColumns
      .flatMap((column) => column.slotTimes.map((timeStr) => parseTimeToMinutes(timeStr)))
      .filter((minutes) => Number.isFinite(minutes));

    if (allSlotMinutes.length === 0) {
      return [];
    }

    const uniqueMinutes = [...new Set(allSlotMinutes)].sort((left, right) => left - right);
    let stepMinutes = Number.POSITIVE_INFINITY;

    for (let index = 1; index < uniqueMinutes.length; index += 1) {
      const delta = uniqueMinutes[index] - uniqueMinutes[index - 1];
      if (delta > 0) {
        stepMinutes = Math.min(stepMinutes, delta);
      }
    }

    const normalizedStep = Number.isFinite(stepMinutes) && stepMinutes > 0 ? stepMinutes : 15;
    const timeline = [];

    for (let minute = uniqueMinutes[0]; minute <= uniqueMinutes[uniqueMinutes.length - 1]; minute += normalizedStep) {
      timeline.push(formatMinutesToTime(minute));
    }

    return timeline;
  }, [professionalScheduleColumns]);
  const groupedScheduleConstraints = useMemo(() => {
    let previousEndMinute = null;
    let canContinueSequence = true;

    return professionalScheduleColumns.reduce((constraints, column, index) => {
      const minimumStartMinute = index === 0 || !canContinueSequence ? null : previousEndMinute;
      const isLocked = index > 0 && (!canContinueSequence || previousEndMinute === null);

      constraints[column.cacheKey] = {
        minimumStartMinute,
        isLocked,
      };

      const selectedTime = selectedGroupTimeSlots[column.cacheKey];
      const selectedMinute = selectedTime ? parseTimeToMinutes(selectedTime) : null;
      const isValidSelection = Boolean(
        selectedTime
        && column.slotTimesSet.has(selectedTime)
        && !isLocked
        && (minimumStartMinute === null || selectedMinute >= minimumStartMinute)
      );

      if (isValidSelection) {
        previousEndMinute = selectedMinute + column.durationMinutes;
        canContinueSequence = true;
      } else {
        previousEndMinute = null;
        canContinueSequence = false;
      }

      return constraints;
    }, {});
  }, [professionalScheduleColumns, selectedGroupTimeSlots]);

  const teamMemberIdsForApi = useMemo(() => {
    // Empty = any professional who can do the services. When a plan restricts
    // professionals, pass those IDs so availability does not include others.
    if (professionalMode === 'any') {
      return planTeamMemberIdSet ? eligibleTeamMembers.map((member) => member.id) : [];
    }
    if (professionalMode === 'specific') return specificTeamMemberId ? [specificTeamMemberId] : [];
    const ids = Object.values(perServiceAssignments).filter(Boolean);
    return [...new Set(ids)];
  }, [professionalMode, specificTeamMemberId, perServiceAssignments, planTeamMemberIdSet, eligibleTeamMembers]);

  const getProfessionalOptionsForService = (service, currentAssignments = perServiceAssignments) => {
    const candidates = filterTeamMembersByPlan(
      service?.isCombo
        ? (teamMembers || []).filter((member) => memberCanPerformBookingService(member, service))
        : getTeamMembersWhoCanDoService(teamMembers, service.id),
      entitlementLock?.teamMemberIds
    );

    if (!isNonStandaloneSecondaryBookingService(service)) {
      return candidates;
    }

    const selectedCompanionProfessionalIds = [...new Set(
      orderedSelectedServices
        .filter((candidate) => candidate.id !== service.id && getBookingServicePriority(candidate) < 2)
        .map((candidate) => currentAssignments[candidate.id])
        .filter(Boolean)
    )];

    if (selectedCompanionProfessionalIds.length === 0) {
      return [];
    }

    return candidates.filter((member) => selectedCompanionProfessionalIds.includes(member.id));
  };

  useEffect(() => {
    if (professionalMode === 'perService') {
      const validGroupKeys = new Set(professionalScheduleGroups.map((group) => group.cacheKey));

      setSelectedGroupTimeSlots((prev) => {
        const nextEntries = Object.entries(prev).filter(([key]) => validGroupKeys.has(key));
        return nextEntries.length === Object.keys(prev).length ? prev : Object.fromEntries(nextEntries);
      });

      setGroupAvailabilityByDate((prev) => {
        const nextEntries = Object.entries(prev).filter(([key]) => validGroupKeys.has(key));
        return nextEntries.length === Object.keys(prev).length ? prev : Object.fromEntries(nextEntries);
      });

      return;
    }

    setSelectedGroupTimeSlots((prev) => (Object.keys(prev).length === 0 ? prev : {}));
    setGroupAvailabilityByDate((prev) => (Object.keys(prev).length === 0 ? prev : {}));
  }, [professionalMode, professionalScheduleGroups]);

  useEffect(() => {
    if (location.state?.services && location.state?.teamMembers) return;
    if (!storeId) return;
    let cancelled = false;
    setLoadingData(true);
    Promise.all([
      StoreService.getServices(true),
      ServiceComboService.getServiceComboCatalog(storeId).catch(() => []),
      api.get(`/team-members?storeId=${storeId}&activeOnly=true`).then((r) => r.data),
      ServiceCategoryService.getActiveCategories(storeId),
      getStoreMembershipPlans(storeId).catch(() => []),
      getCustomerMemberships(storeId).catch(() => []),
    ])
      .then(([svc, combos, tm, categories, plans, memberships]) => {
        if (!cancelled) {
          setServices(mergeCatalogCombos(svc, combos));
          setTeamMembers(filterAgendaVisibleTeamMembers(tm));
          setServiceCategories(categories || []);
          setMembershipPlans(plans || []);
          setCustomerMemberships(memberships || []);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setServices([]);
          setTeamMembers([]);
          setServiceCategories([]);
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingData(false);
      });
    return () => { cancelled = true; };
  }, [storeId, location.state?.services, location.state?.teamMembers]);

  useEffect(() => {
    if (!storeId) return;
    if (!(location.state?.services && location.state?.teamMembers)) return;

    let cancelled = false;
    ServiceCategoryService.getActiveCategories(storeId)
      .then((categories) => {
        if (!cancelled) setServiceCategories(categories || []);
      })
      .catch(() => {
        if (!cancelled) setServiceCategories([]);
      });

    return () => { cancelled = true; };
  }, [storeId, location.state?.services, location.state?.teamMembers]);

  // Packages already paid for stay usable even if the store has since dropped to Free.
  useEffect(() => {
    if (!storeId) {
      setCustomerServicePackages([]);
      return undefined;
    }

    let cancelled = false;
    getCustomerServicePackages(storeId)
      .then((packages) => {
        if (!cancelled) setCustomerServicePackages(packages || []);
      })
      .catch(() => {
        if (!cancelled) setCustomerServicePackages([]);
      });

    return () => { cancelled = true; };
  }, [storeId]);

  useEffect(() => {
    if (!storeId) {
      return undefined;
    }

    let cancelled = false;
    if (isFreePlan) {
      setCatalogProducts([]);
      return undefined;
    }
    requestDeduplicator.deduplicate(`product-catalog-${storeId}`, () => (
      ProductService.getProductCatalog(storeId)
    )).then((items) => {
      if (!cancelled) {
        setCatalogProducts(items || []);
      }
    }).catch(() => {
      if (!cancelled) {
        setCatalogProducts([]);
      }
    });

    return () => { cancelled = true; };
  }, [storeId, isFreePlan]);

  // Apply pre-selected services from navigation state once services are loaded (handles async case)
  useEffect(() => {
    if (preSelectionAppliedRef.current) return;
    if (!services || !location.state?.preSelectedServiceIds) return;
    const preSelected = services.filter((service) => location.state.preSelectedServiceIds.includes(service.id));
    const needsStore = preSelected.find((service) => !hasCustomerBookingDuration(service));
    if (needsStore) {
      preSelectionAppliedRef.current = true;
      openChat(t('customer.appointments.booking.serviceInterestDraft', {
        serviceName: translateServiceName(needsStore.name),
      }));
    }
    if (preSelected.length > 0) {
      preSelectionAppliedRef.current = true;
      setSelectedServices(selectCustomerBookableServices(services, location.state.preSelectedServiceIds));
    }
  }, [services]); // eslint-disable-line react-hooks/exhaustive-deps

  // Apply membership plan preselection from /memberships (or similar) once plans + services are loaded
  useEffect(() => {
    if (membershipPlanPreselectionAppliedRef.current) return;
    const planId = location.state?.preSelectedMembershipPlanId;
    if (!planId || !services?.length || !membershipPlans.length) return;

    const plan = membershipPlans.find((candidate) => candidate.id === planId);
    if (!plan) return;

    membershipPlanPreselectionAppliedRef.current = true;
    setSelectedMembershipPlanId(plan.id);
    setSelectedServices((services || []).filter((service) => (plan.serviceIds || []).includes(service.id)));
    setActiveStep(STEP_PROFESSIONALS);
  }, [services, membershipPlans, location.state?.preSelectedMembershipPlanId]);

  // "Book with this package" on the packages page lands here with the package already chosen.
  useEffect(() => {
    if (servicePackagePreselectionAppliedRef.current) return;
    const packageId = location.state?.preSelectedServicePackageId;
    if (!packageId || !services?.length || !usableServicePackages.length) return;

    const servicePackage = usableServicePackages.find((candidate) => candidate.id === packageId);
    if (!servicePackage) return;

    servicePackagePreselectionAppliedRef.current = true;
    setSelectedServicePackageId(servicePackage.id);
    setSelectedServices((services || []).filter((service) => servicePackage.includedServiceIds.includes(service.id)));
    setActiveStep(STEP_PROFESSIONALS);
  }, [services, usableServicePackages, location.state?.preSelectedServicePackageId]);

  useEffect(() => {
    if (professionalMode !== 'perService' || selectedServiceIds.length === 0) {
      return;
    }

    setPerServiceAssignments((prev) => {
      const next = {};

      orderedSelectedServices.forEach((service) => {
        const availableOptions = getProfessionalOptionsForService(service, { ...prev, ...next });
        const availableOptionIds = availableOptions.map((teamMember) => teamMember.id);
        const preferredTeamMemberId = [prev[service.id], suggestedAssignment[service.id]].find((teamMemberId) => availableOptionIds.includes(teamMemberId));

        next[service.id] = preferredTeamMemberId || '';
      });

      const previousKeys = Object.keys(prev).sort();
      const nextKeys = Object.keys(next).sort();
      const hasSameKeys = previousKeys.length === nextKeys.length && previousKeys.every((key, index) => key === nextKeys[index]);
      const hasSameValues = hasSameKeys && nextKeys.every((key) => next[key] === prev[key]);

      return hasSameValues ? prev : next;
    });
  }, [orderedSelectedServices, professionalMode, selectedServiceIds.length, suggestedAssignment, teamMembers]); // eslint-disable-line react-hooks/exhaustive-deps

  const bookingViaMembership = Boolean(selectedMembershipPlanId);
  const cameFromMembershipsPage = Boolean(location.state?.preSelectedMembershipPlanId);
  const bookingViaServicePackage = Boolean(selectedServicePackageId);
  const cameFromPackagesPage = Boolean(location.state?.preSelectedServicePackageId);
  const originPageRoute = (bookingViaMembership && cameFromMembershipsPage && 'memberships')
    || (bookingViaServicePackage && cameFromPackagesPage && 'packages')
    || null;

  const goBackFromCalendar = () => {
    if (skipProfessionalsStep) {
      if (originPageRoute) {
        navigate(buildCustomerRoute(storeId, originPageRoute));
        return;
      }
      setActiveStep(STEP_SERVICES);
      return;
    }
    setActiveStep(STEP_PROFESSIONALS);
  };

  const handleBackFromProfessionals = () => {
    if (originPageRoute) {
      navigate(buildCustomerRoute(storeId, originPageRoute));
      return;
    }
    setActiveStep(STEP_SERVICES);
  };

  const handleToggleMembershipPlan = (plan) => {
    if (selectedMembershipPlanId === plan.id) {
      setSelectedMembershipPlanId(null);
      setSelectedServices([]);
      return;
    }

    setSelectedServicePackageId(null);
    setSelectedMembershipPlanId(plan.id);
    const planServices = (services || []).filter((s) => plan.serviceIds.includes(s.id));
    setSelectedServices(planServices);
    setActiveStep(STEP_PROFESSIONALS);
  };

  const handleToggleServicePackage = (servicePackage) => {
    if (selectedServicePackageId === servicePackage.id) {
      setSelectedServicePackageId(null);
      setSelectedServices([]);
      setSelectedPaymentMethod('inStore');
      return;
    }

    setSelectedMembershipPlanId(null);
    setSelectedServicePackageId(servicePackage.id);
    setSelectedServices((services || []).filter((service) => servicePackage.includedServiceIds.includes(service.id)));
    setActiveStep(STEP_PROFESSIONALS);
  };

  const handleToggleService = (service) => {
    if (!hasCustomerBookingDuration(service)) {
      openChat(t('customer.appointments.booking.serviceInterestDraft', {
        serviceName: translateServiceName(service.name),
      }));
      return;
    }

    if (entitlementLock) {
      const planServiceIds = entitlementLock.serviceIds;
      if (planServiceIds.includes(service.id)) {
        return;
      }

      setSelectedServices((prev) => (
        prev.some((s) => s.id === service.id)
          ? prev.filter((s) => s.id !== service.id)
          : [...prev, service]
      ));
      return;
    }

    setSelectedServices((prev) => matchServiceCombos(
      prev.some((s) => s.id === service.id)
        ? prev.filter((s) => s.id !== service.id)
        : [...prev, service],
      activeServices,
    ));
  };

  const handleProfessionalModeChange = (e) => {
    const mode = e.target.value;
    setProfessionalMode(mode);
    if (mode === 'specific') {
      setSpecificTeamMemberId(canDoAll.length === 1 ? canDoAll[0].id : '');
    }
    if (mode === 'perService') setPerServiceAssignments({});
  };

  const canProceedFromProfessionals = useMemo(() => {
    if (hasInvalidSecondarySelection) return false;
    if (professionalMode === 'any') return true;
    if (professionalMode === 'specific') return Boolean(specificTeamMemberId);
    return selectedServiceIds.every((sid) => perServiceAssignments[sid]);
  }, [hasInvalidSecondarySelection, professionalMode, specificTeamMemberId, selectedServiceIds, perServiceAssignments]);

  useEffect(() => {
    if (activeStep !== STEP_PROFESSIONALS) return;
    if (!skipProfessionalsStep || !canDoAll[0]) return;
    setProfessionalMode('specific');
    setSpecificTeamMemberId(canDoAll[0].id);
    setActiveStep(STEP_CALENDAR);
  }, [activeStep, canDoAll, skipProfessionalsStep]);

  useEffect(() => {
    if (professionalMode !== 'specific' || !specificOptionDisabled) return;

    setProfessionalMode('perService');
    setSpecificTeamMemberId('');
    setPerServiceAssignments({ ...suggestedAssignment });
  }, [professionalMode, specificOptionDisabled, suggestedAssignment]);

  const getTodayStr = () => {
    const t = new Date();
    return `${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, '0')}-${String(t.getDate()).padStart(2, '0')}`;
  };

  const specialOpeningDays = useMemo(
    () => storeOverview?.specialOpeningDays || [],
    [storeOverview?.specialOpeningDays],
  );
  const teamMemberWorkingHours = useMemo(
    () => storeOverview?.teamMemberWorkingHours || [],
    [storeOverview?.teamMemberWorkingHours],
  );
  const isClosedOnDate = useCallback((dateStr, memberIds) => isStoreClosedOnDate(
    dateStr,
    storeOverview?.openingHours,
    authorizationHoursForPlan,
    specialOpeningDays,
    memberIds,
    teamMemberWorkingHours,
  ), [storeOverview?.openingHours, authorizationHoursForPlan, specialOpeningDays, teamMemberWorkingHours]);

  const DAYS_STRIP_TOTAL = 42;
  const DATE_CARD_MIN = 52;
  const DATE_CARD_GAP = 8;
  const DATE_STRIP_BUTTONS_AND_GAPS = 76;
  const daysPerPage = (() => {
    if (!datesRowWidth) return 3;
    const available = datesRowWidth - DATE_STRIP_BUTTONS_AND_GAPS;
    const fit = Math.floor((available + DATE_CARD_GAP) / (DATE_CARD_MIN + DATE_CARD_GAP));
    return fit >= 7 ? 7 : fit >= 5 ? 5 : 3;
  })();
  const calendarStripDates = useMemo(() => {
    const arr = [];
    const t = new Date();
    for (let i = 0; i < DAYS_STRIP_TOTAL; i++) {
      const d = new Date(t);
      d.setDate(d.getDate() + i);
      arr.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`);
    }
    return arr;
  }, []);
  const maxStripPage = Math.max(0, Math.ceil(calendarStripDates.length / daysPerPage) - 1);
  const visibleStripDates = useMemo(
    () => calendarStripDates.slice(stripPage * daysPerPage, stripPage * daysPerPage + daysPerPage),
    [calendarStripDates, stripPage, daysPerPage]
  );

  useEffect(() => {
    if (stripPage > maxStripPage) setStripPage(maxStripPage);
  }, [stripPage, maxStripPage]);

  useEffect(() => {
    if (activeStep !== STEP_CALENDAR || !dateStripRowRef.current) return;
    const el = dateStripRowRef.current;
    const observer = new ResizeObserver((entries) => {
      const { width } = entries[0]?.contentRect ?? {};
      if (typeof width === 'number') setDatesRowWidth(width);
    });
    observer.observe(el);
    setDatesRowWidth(el.getBoundingClientRect().width);
    return () => observer.disconnect();
  }, [activeStep]);

  useEffect(() => {
    if (activeStep !== STEP_CALENDAR) return;
    setSelectedDate((prev) => prev || getTodayStr());
    setStripPage(0);
    setAvailabilityByDate({});
    setGroupAvailabilityByDate({});
    setSelectedTimeSlot(null);
    setSelectedTeamMemberIdForSlot(null);
    setSelectedGroupTimeSlots({});
    loadedDatesRef.current = new Set();
  }, [activeStep]);

  useEffect(() => {
    if (activeStep !== STEP_CALENDAR || !selectedDate) return;
    setSelectedTimeSlot(null);
    setSelectedTeamMemberIdForSlot(null);
    setSelectedGroupTimeSlots({});
  }, [activeStep, selectedDate]);

  useEffect(() => {
    loadedDatesRef.current = new Set();
    setAvailabilityByDate({});
    setGroupAvailabilityByDate({});
  }, [storeOverview?.openingHours, authorizationHoursForPlan, specialOpeningDays, teamMemberWorkingHours]);

  useEffect(() => {
    if (!showGroupedProfessionalSchedules) {
      return;
    }

    setSelectedGroupTimeSlots((prev) => {
      const nextSelections = {};

      professionalScheduleColumns.forEach((column) => {
        const selectedTime = prev[column.cacheKey];
        if (!selectedTime) {
          return;
        }

        const constraint = groupedScheduleConstraints[column.cacheKey];
        const selectedMinute = parseTimeToMinutes(selectedTime);
        const isValidSelection = column.slotTimesSet.has(selectedTime)
          && !constraint?.isLocked
          && (constraint?.minimumStartMinute == null || selectedMinute >= constraint.minimumStartMinute);

        if (isValidSelection) {
          nextSelections[column.cacheKey] = selectedTime;
        }
      });

      const previousKeys = Object.keys(prev).sort();
      const nextKeys = Object.keys(nextSelections).sort();
      const hasSameKeys = previousKeys.length === nextKeys.length && previousKeys.every((key, index) => key === nextKeys[index]);
      const hasSameValues = hasSameKeys && nextKeys.every((key) => nextSelections[key] === prev[key]);

      return hasSameValues ? prev : nextSelections;
    });
  }, [groupedScheduleConstraints, professionalScheduleColumns, showGroupedProfessionalSchedules]);

  useEffect(() => {
    if (
      activeStep !== STEP_CALENDAR ||
      professionalMode === 'perService' ||
      !selectedDate ||
      !storeId ||
      selectedServiceIds.length === 0 ||
      teamMemberIdsForApi === undefined ||
      loadedDatesRef.current.has(selectedDate)
    )
      return;
    if (isClosedOnDate(selectedDate, teamMemberIdsForApi)) {
      loadedDatesRef.current.add(selectedDate);
      setAvailabilityByDate((prev) => ({ ...prev, [selectedDate]: [] }));
      return;
    }
    let cancelled = false;
    setLoadingAvailability(true);
    const dateToFetch = selectedDate;
    getAvailability(selectedDate, teamMemberIdsForApi, selectedCatalogServiceIds, selectedComboIds)
      .then((data) => {
        if (!cancelled) {
          loadedDatesRef.current.add(dateToFetch);
          setHasDifferentialPricing(Boolean(data?.hasDifferentialPricing));
          setAvailabilityByDate((prev) => ({ ...prev, [dateToFetch]: filterBookingSlotsForPlan(data?.timeSlots ?? [], isFreePlan) }));
        }
      })
      .catch(() => {
        if (!cancelled) {
          loadedDatesRef.current.add(dateToFetch);
          setAvailabilityByDate((prev) => ({ ...prev, [dateToFetch]: [] }));
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingAvailability(false);
      });
    return () => { cancelled = true; };
  }, [activeStep, professionalMode, storeId, selectedDate, selectedServiceIds, selectedCatalogServiceIds, selectedComboIds, teamMemberIdsForApi, storeOverview?.openingHours, authorizationHoursForPlan, specialOpeningDays, teamMemberWorkingHours, isFreePlan, isClosedOnDate]);

  useEffect(() => {
    if (activeStep !== STEP_REVIEW || !storeId || selectedCatalogServiceIds.length === 0) {
      setBookingEntitlements([]);
      return undefined;
    }

    let cancelled = false;
    setLoadingBookingEntitlements(true);

    getBookingMembershipEntitlements(storeId, selectedCatalogServiceIds)
      .then((entitlements) => {
        if (cancelled) {
          return;
        }

        setBookingEntitlements(entitlements);

        const eligible = entitlements.filter((entitlement) => entitlement.canRedeem);

        setSelectedPaymentMethod((current) => {
          if (selectedMembershipPlanId) {
            return current;
          }

          if (current.startsWith('membership:')
            && !eligible.some((entitlement) => `membership:${entitlement.customerMembershipId}` === current)) {
            return 'inStore';
          }

          return current;
        });
      })
      .catch(() => {
        if (!cancelled) {
          setBookingEntitlements([]);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoadingBookingEntitlements(false);
        }
      });

    return () => { cancelled = true; };
  }, [activeStep, storeId, selectedCatalogServiceIds, selectedMembershipPlanId]);

  useEffect(() => {
    if (activeStep !== STEP_REVIEW || !storeId || selectedCatalogServiceIds.length === 0 || usableServicePackages.length === 0) {
      setPackageEntitlements([]);
      return undefined;
    }

    let cancelled = false;

    getBookingServicePackageEntitlements(storeId, selectedCatalogServiceIds, selectedDate)
      .then((entitlements) => {
        if (cancelled) {
          return;
        }

        setPackageEntitlements(entitlements);
        const redeemable = entitlements.filter((entitlement) => entitlement.canRedeem);

        setSelectedPaymentMethod((current) => {
          if (current.startsWith(PACKAGE_PAYMENT_PREFIX)
            && !redeemable.some((entitlement) => `${PACKAGE_PAYMENT_PREFIX}${entitlement.customerServicePackageId}` === current)) {
            return 'inStore';
          }

          // Only a package the customer picked on the first step is chosen for them.
          const chosen = selectedServicePackageId
            ? redeemable.find((entitlement) => String(entitlement.customerServicePackageId) === String(selectedServicePackageId))
            : null;
          return chosen ? `${PACKAGE_PAYMENT_PREFIX}${chosen.customerServicePackageId}` : current;
        });
      })
      .catch(() => {
        if (!cancelled) {
          setPackageEntitlements([]);
        }
      });

    return () => { cancelled = true; };
  }, [activeStep, storeId, selectedCatalogServiceIds, selectedDate, selectedServicePackageId, usableServicePackages.length]);

  // Stores that only allow online payment have no "in store" radio to fall back to.
  useEffect(() => {
    if (activeStep !== STEP_REVIEW) {
      return;
    }

    setSelectedPaymentMethod((current) => {
      if (current === 'inStore' && !appointmentAllowsInStore && appointmentAllowsOnline) return 'online';
      if (current === 'online' && !appointmentAllowsOnline && appointmentAllowsInStore) return 'inStore';
      return current;
    });
  }, [activeStep, appointmentAllowsInStore, appointmentAllowsOnline]);

  // When a membership plan is selected and customer has an active membership, auto-select it at review
  useEffect(() => {
    if (activeStep !== STEP_REVIEW || !selectedMembershipPlanId) return;
    if (selectedPlanMembership) {
      setSelectedPaymentMethod(`membership:${selectedPlanMembership.id}`);
    } else {
      setSelectedPaymentMethod('inStore');
    }
  }, [activeStep, selectedMembershipPlanId, selectedPlanMembership]);

  useEffect(() => {
    if (
      activeStep !== STEP_CALENDAR ||
      professionalMode !== 'perService' ||
      !selectedDate ||
      !storeId ||
      professionalScheduleGroups.length === 0
    ) {
      return;
    }

    const groupsToLoad = professionalScheduleGroups.filter((group) => !(selectedDate in (groupAvailabilityByDate[group.cacheKey] || {})));
    if (groupsToLoad.length === 0) {
      return;
    }

    let cancelled = false;
    setLoadingAvailability(true);

    Promise.all(
      groupsToLoad.map(async (group) => {
        if (isClosedOnDate(selectedDate, [group.teamMemberId])) {
          return { cacheKey: group.cacheKey, timeSlots: [] };
        }

        try {
          const data = await getAvailability(
            selectedDate,
            [group.teamMemberId],
            group.services.filter((service) => !service.isCombo).map((service) => service.id),
            group.services.filter((service) => service.isCombo).map((service) => service.id),
          );
          if (data?.hasDifferentialPricing) {
            setHasDifferentialPricing(true);
          }
          return { cacheKey: group.cacheKey, timeSlots: filterBookingSlotsForPlan(data?.timeSlots ?? [], isFreePlan) };
        } catch {
          return { cacheKey: group.cacheKey, timeSlots: [] };
        }
      })
    )
      .then((results) => {
        if (cancelled) {
          return;
        }

        setGroupAvailabilityByDate((prev) => {
          const next = { ...prev };
          results.forEach(({ cacheKey, timeSlots }) => {
            next[cacheKey] = {
              ...(prev[cacheKey] || {}),
              [selectedDate]: timeSlots,
            };
          });
          return next;
        });
      })
      .finally(() => {
        if (!cancelled) {
          setLoadingAvailability(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [activeStep, professionalMode, selectedDate, storeId, professionalScheduleGroups, groupAvailabilityByDate, storeOverview?.openingHours, authorizationHoursForPlan, specialOpeningDays, teamMemberWorkingHours, isFreePlan, isClosedOnDate]);

  const handleDateSelect = (date) => setSelectedDate(date);
  const handleTimeSlotSelect = (date, slot, availableIds, requiresConfirmation = false) => {
    setSelectedDate(date);
    if (professionalMode === 'any' && requiresConfirmation) {
      setSelectedTimeSlot(slot);
      setSelectedTeamMemberIdForSlot(null);
      return;
    }
    if (professionalMode === 'any' && availableIds && availableIds.length >= 2) {
      setPickerSlot({ date, timeSlot: slot, availableTeamMemberIds: availableIds });
      setPickerOptions(availableIds.map((id) => teamMembers.find((m) => m.id === id)).filter(Boolean));
      setPickerModalOpen(true);
    } else if (professionalMode === 'any' && availableIds?.length === 1) {
      const singleId = availableIds[0];
      const professional = teamMembers?.find((m) => m.id === singleId);
      if (professional && selectedServiceIds.length > 0) {
        const professionalServiceIds = new Set((professional.services || []).map((s) => s.id));
        const cannotDo = orderedSelectedServices.filter((service) => !professionalServiceIds.has(service.id));
        if (cannotDo.length > 0) {
          setIncompatibleProfessionalDialog({ name: professional.name, services: cannotDo });
          return;
        }
      }
      setSelectedTimeSlot(slot);
      setSelectedTeamMemberIdForSlot(singleId);
    } else {
      setSelectedTimeSlot(slot);
      setSelectedTeamMemberIdForSlot(null);
    }
  };

  const handlePickerConfirm = (teamMemberId) => {
    const professional = teamMembers?.find((m) => m.id === teamMemberId);
    if (professional && selectedServiceIds.length > 0) {
      const professionalServiceIds = new Set((professional.services || []).map((s) => s.id));
      const cannotDo = orderedSelectedServices.filter((service) => !professionalServiceIds.has(service.id));
      if (cannotDo.length > 0) {
        setPickerModalOpen(false);
        setPickerSlot(null);
        setPickerOptions([]);
        setIncompatibleProfessionalDialog({ name: professional.name, services: cannotDo });
        return;
      }
    }
    if (pickerSlot) {
      setSelectedDate(pickerSlot.date);
      setSelectedTimeSlot(pickerSlot.timeSlot);
      setSelectedTeamMemberIdForSlot(teamMemberId);
    }
    setPickerModalOpen(false);
    setPickerSlot(null);
    setPickerOptions([]);
  };

  const getTeamMemberNameById = (teamMemberId) => {
    return teamMembers?.find((m) => m.id === teamMemberId)?.name || '';
  };

  const getSelectedServiceNameById = (serviceId) => {
    const service = orderedSelectedServices.find((item) => item.id === serviceId);
    return service ? getServiceName(service.name, lang, storeLanguage) : String(serviceId);
  };

  const handleGroupedTimeSlotSelect = (groupCacheKey, timeStr) => {
    const constraint = groupedScheduleConstraints[groupCacheKey];
    const selectedMinute = parseTimeToMinutes(timeStr);

    if (constraint?.isLocked || (constraint?.minimumStartMinute != null && selectedMinute < constraint.minimumStartMinute)) {
      return;
    }

    setSelectedGroupTimeSlots((prev) => ({
      ...prev,
      [groupCacheKey]: timeStr,
    }));
  };

  const selectedSlotRequiresConfirmation = useMemo(() => {
    if (!selectedDate) {
      return false;
    }

    const slotRequiresConfirmation = (slots, time) => (
      (slots || []).some((slot) => {
        const timeStr = typeof slot.timeSlot === 'string' ? slot.timeSlot : String(slot.timeSlot ?? '');
        return timeStr === time && Boolean(slot.requiresConfirmation);
      })
    );

    if (professionalMode === 'perService') {
      return professionalScheduleGroups.some((group) => {
        const time = selectedGroupTimeSlots[group.cacheKey];
        if (!time) {
          return false;
        }
        return slotRequiresConfirmation(groupAvailabilityByDate[group.cacheKey]?.[selectedDate], time);
      });
    }

    if (!selectedTimeSlot) {
      return false;
    }

    return slotRequiresConfirmation(availabilityByDate[selectedDate], selectedTimeSlot);
  }, [
    availabilityByDate,
    groupAvailabilityByDate,
    professionalMode,
    professionalScheduleGroups,
    selectedDate,
    selectedGroupTimeSlots,
    selectedTimeSlot,
  ]);

  const selectedSlotAdditionalCost = useMemo(() => {
    const slotAdditionalCost = (slots, time) => {
      const match = (slots || []).find((slot) => {
        const timeStr = typeof slot.timeSlot === 'string' ? slot.timeSlot : String(slot.timeSlot ?? '');
        return timeStr === time;
      });
      return Number(match?.additionalCostAmount || 0);
    };

    if (!selectedDate) {
      return 0;
    }

    if (professionalMode === 'perService') {
      const earliest = professionalScheduleGroups
        .map((group) => selectedGroupTimeSlots[group.cacheKey])
        .filter(Boolean)
        .sort()[0];
      if (!earliest) {
        return 0;
      }
      const group = professionalScheduleGroups.find((item) => selectedGroupTimeSlots[item.cacheKey] === earliest);
      return slotAdditionalCost(groupAvailabilityByDate[group?.cacheKey]?.[selectedDate], earliest);
    }

    if (!selectedTimeSlot) {
      return 0;
    }

    return slotAdditionalCost(availabilityByDate[selectedDate], selectedTimeSlot);
  }, [
    availabilityByDate,
    groupAvailabilityByDate,
    professionalMode,
    professionalScheduleGroups,
    selectedDate,
    selectedGroupTimeSlots,
    selectedTimeSlot,
  ]);

  const getFinalServiceAssignments = () => {
    if (professionalMode === 'any' && selectedTimeSlot) {
      if (selectedTeamMemberIdForSlot) {
        return buildSequentialAssignments(orderedSelectedServices, selectedTeamMemberIdForSlot, selectedTimeSlot);
      }
      if (selectedSlotRequiresConfirmation) {
        return buildOpenProfessionalAssignments(orderedSelectedServices, selectedTimeSlot);
      }
      return [];
    }
    if (professionalMode === 'specific') {
      return buildSequentialAssignments(orderedSelectedServices, specificTeamMemberId, selectedTimeSlot);
    }
    return professionalScheduleGroups.flatMap((group) => (
      buildSequentialAssignments(group.services, group.teamMemberId, selectedGroupTimeSlots[group.cacheKey])
    ));
  };

  const reviewAssignmentsKey = activeStep === STEP_REVIEW
    ? JSON.stringify(getFinalServiceAssignments())
    : '';
  // Services the chosen membership covers owe nothing up front, so they drop out of the quote.
  const coveringMembershipId = selectedMembershipPlanId ? selectedPlanMembership?.id : selectedMembershipId;
  const coveredServiceIdsKey = useMemo(() => {
    if (selectedPackagePaymentId) {
      const packageEntitlement = (packageEntitlements || []).find(
        (item) => String(item.customerServicePackageId) === String(selectedPackagePaymentId),
      );
      return packageEntitlement?.canRedeem ? (packageEntitlement.coveredServiceIds || []).join(',') : '';
    }

    const entitlement = (bookingEntitlements || []).find(
      (item) => coveringMembershipId && String(item.customerMembershipId) === String(coveringMembershipId),
    );
    if (!entitlement) {
      return '';
    }

    return (entitlement.canCoverAllServices ? selectedServiceIds : (entitlement.coveredServiceIds || [])).join(',');
  }, [bookingEntitlements, coveringMembershipId, selectedServiceIds, packageEntitlements, selectedPackagePaymentId]);
  const pendingMembershipEnrollment = Boolean(selectedMembershipPlanId && !selectedPlanMembership);
  const bookingProductLines = useMemo(() => (
    productLines.map((line) => ({ productId: line.productId, quantity: line.quantity }))
  ), [productLines]);
  // Products picked with the booking are paid online with it, so they change the quote.
  const productLinesKey = JSON.stringify(bookingProductLines);

  useEffect(() => {
    if (!reviewAssignmentsKey || !appointmentAllowsOnline || !storeId || !selectedDate) {
      setPrepaymentQuote(null);
      setPrepaymentQuoteFailed(false);
      setLoadingPrepaymentQuote(false);
      return undefined;
    }

    const serviceAssignments = JSON.parse(reviewAssignmentsKey);
    if (serviceAssignments.length === 0) {
      // A quote for the previous selection no longer applies, nor does its spinner.
      setPrepaymentQuote(null);
      setLoadingPrepaymentQuote(false);
      return undefined;
    }

    let cancelled = false;
    setLoadingPrepaymentQuote(true);
    setPrepaymentQuoteFailed(false);

    getPrepaymentQuote({
      storeId,
      date: selectedDate,
      serviceAssignments,
      coveredServiceIds: coveredServiceIdsKey ? coveredServiceIdsKey.split(',').map(Number) : [],
      pendingMembershipEnrollment,
      products: JSON.parse(productLinesKey),
      language: lang,
    })
      .then((quote) => {
        if (!cancelled) setPrepaymentQuote(quote);
      })
      .catch(() => {
        if (!cancelled) {
          setPrepaymentQuote(null);
          setPrepaymentQuoteFailed(true);
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingPrepaymentQuote(false);
      });

    return () => { cancelled = true; };
  }, [reviewAssignmentsKey, appointmentAllowsOnline, storeId, selectedDate, coveredServiceIdsKey, pendingMembershipEnrollment, productLinesKey, lang]);

  // An unset price cannot be charged now. A deposit that is still required is collected once every
  // price is set. A membership that falls back to in-store is moved online when that is the only way to pay.
  useEffect(() => {
    if (hasUnpricedSelection) {
      setSelectedPaymentMethod((current) => (current === 'online' ? 'inStore' : current));
      return;
    }
    if (prepaymentRequired && selectedPaymentMethod === 'inStore') {
      setSelectedPaymentMethod('online');
    }
  }, [hasUnpricedSelection, prepaymentRequired, selectedPaymentMethod]);

  // A membership that leaves a service with a deposit uncovered still pays that part online; the
  // quote is already computed with the membership's coverage, so it only asks for what is left.
  const bookingChargesAmount = calculateBookingReviewAmountDue({
    selectedServices: orderedSelectedServices,
    additionalCostAmount: selectedSlotAdditionalCost,
    productsTotal: getProductLinesTotal(catalogProducts, productLines),
  });
  const nothingToPay = bookingChargesAmount <= 0;
  const payingOnline = customerBookingPaysOnline({
    nothingToPay,
    hasUnpricedSelection,
    selectedPaymentMethod,
    prepaymentRequired,
    appointmentAllowsOnline,
    usesEntitlement: Boolean(selectedMembershipId) || Boolean(selectedPackagePaymentId),
  });
  const activePaymentOption = payingOnline ? resolveActivePaymentOption(prepaymentQuote, payInFull) : null;
  const requiresPolicyAcceptance = Boolean(activePaymentOption && activePaymentOption.nonRefundableAmount > 0);

  // Consent covers the exact terms that were shown; once they change it has to be given again.
  useEffect(() => {
    setPolicyAccepted(false);
  }, [activePaymentOption?.policyText]);

  const getAssignmentSummaryItems = () => {
    const assignments = getFinalServiceAssignments();

    if (professionalMode === 'any') {
      const anyName = selectedTeamMemberIdForSlot ? getTeamMemberNameById(selectedTeamMemberIdForSlot) : '';
      return anyName ? [anyName] : [t('customer.appointments.booking.anyProfessional')];
    }

    const detailedAssignments = assignments
      .map((assignment) => {
        const professionalName = getTeamMemberNameById(assignment.teamMemberId);
        const serviceName = getSelectedServiceNameById(assignment.serviceId);
        return professionalName ? `${serviceName}: ${professionalName}` : '';
      })
      .filter(Boolean);

    return detailedAssignments.length ? detailedAssignments : ['—'];
  };

  const resolveRenewalPeriodEndUtc = () => {
    if (selectedPlanMembership?.currentPeriodEndUtc) {
      return selectedPlanMembership.currentPeriodEndUtc;
    }
    return getSelectedMembershipPeriodEndUtc(
      selectedMembershipId,
      bookingEntitlements,
      customerMemberships
    );
  };

  const promptRenewalWarningIfNeeded = (continueAction) => {
    const periodEndUtc = resolveRenewalPeriodEndUtc();
    if (!isMembershipRenewalRisk(selectedDate, periodEndUtc)) {
      return false;
    }
    setRenewalPeriodEndDisplay(String(periodEndUtc).slice(0, 10));
    setRenewalContinueAction(continueAction);
    setRenewalWarningOpen(true);
    return true;
  };

  const openProductDialog = () => setProductDialogOpen(true);

  const goToReviewStep = () => {
    setActiveStep(STEP_REVIEW);
    if (!isFreePlan && catalogProducts.length > 0) {
      openProductDialog();
    }
  };

  const handleConfirm = async () => {
    if (
      selectedMembershipId
      && !renewalAcknowledgedRef.current
      && promptRenewalWarningIfNeeded('confirm')
    ) {
      return;
    }

    renewalAcknowledgedRef.current = false;
    setError(null);
    setSubmitting(true);
    const assignments = getFinalServiceAssignments();
    if (assignments.length === 0 || assignments.length !== selectedServiceIds.length) {
      setError(t('customer.appointments.booking.error'));
      setSubmitting(false);
      return;
    }

    const enrollingMembershipPlanId = selectedMembershipPlanId && !selectedPlanMembership
      ? selectedMembershipPlanId
      : undefined;
    let consent;
    try {
      consent = await ensureConsents({
        serviceIds: assignments.map((assignment) => assignment.serviceId).filter(Boolean),
        serviceComboIds: assignments.map((assignment) => assignment.serviceComboId).filter(Boolean),
        membershipPlanId: enrollingMembershipPlanId,
        contextType: ConsentContextType.Booking,
      });
    } catch (consentError) {
      console.error('Failed to check consent forms:', consentError);
      setError(t('consentForms.errors.load'));
      setSubmitting(false);
      return;
    }

    if (!consent.accepted) {
      setSubmitting(false);
      return;
    }

    try {
      const response = await createAppointment(buildCustomerBookingRequest({
        date: selectedDate,
        assignments,
        detailsFromCustomer,
        selectedMembershipPlanId,
        selectedPlanMembership,
        selectedMembershipId,
        selectedPackagePaymentId,
        bookingProductLines,
        payingOnline,
        payInFull,
        policyAccepted,
        policyLanguage: payingOnline ? toPolicyLanguage(lang) : undefined,
        consentAcceptanceBatchId: consent.acceptanceBatchId,
      }));
      const confirmResult = resolveBookingConfirmResult(response, { payingOnline });
      if (confirmResult.type === 'awaitingConfirmation') {
        setSuccessMessage(t('customer.appointments.booking.awaitingConfirmationSuccess'));
        setTimeout(() => navigate(buildCustomerRoute(storeId, 'my-appointments')), 2000);
        return;
      }
      if (confirmResult.type === 'membership') {
        setSuccessMessage(t('customer.appointments.booking.membershipSuccess'));
        setTimeout(() => navigate(buildCustomerRoute(storeId, '')), 2000);
        return;
      }
      if (confirmResult.type === 'package') {
        setSuccessMessage(t('customer.appointments.booking.packageSuccess'));
        setTimeout(() => navigate(buildCustomerRoute(storeId, '')), 2000);
        return;
      }
      if (confirmResult.type === 'checkout') {
        checkoutFinishedRef.current = false;
        checkoutSessionRef.current = null;
        setCheckoutAppointmentId(confirmResult.appointmentId);
        return;
      }
      setSuccessMessage(t('customer.appointments.booking.success'));
      setTimeout(() => navigate(buildCustomerRoute(storeId, '')), 2000);
    } catch (err) {
      const apiCode = getApiErrorCode(err);
      setError(BOOKING_ERROR_KEYS[apiCode]
        ? t(BOOKING_ERROR_KEYS[apiCode])
        : (err.response?.data?.message || t('customer.appointments.booking.error')));
    } finally {
      setSubmitting(false);
    }
  };

  const showBookingConfirmed = () => {
    setError(null);
    setSuccessMessage(t('customer.appointments.booking.success'));
    setTimeout(() => navigate(buildCustomerRoute(storeId, '')), 2000);
  };

  const handleCheckoutComplete = async () => {
    if (checkoutFinishedRef.current) return;
    checkoutFinishedRef.current = true;
    const sessionId = checkoutSessionRef.current?.sessionId;
    setCheckoutAppointmentId(null);

    if (!sessionId) {
      setSuccessMessage(null);
      setError(t('customer.appointments.booking.paymentNotCompleted'));
      return;
    }

    try {
      const { outcome, bookingReleased } = await getAppointmentCheckoutOutcome(sessionId);
      const paymentResult = resolveBookingPaymentOutcome({ outcome, bookingReleased });
      if (paymentResult.type === 'paid') {
        showBookingConfirmed();
        return;
      }
      if (paymentResult.type === 'failed') {
        setSuccessMessage(null);
        setError(t('customer.appointments.booking.paymentNotCompleted'));
        return;
      }
      navigate(buildCustomerRoute(storeId, 'my-appointments'), { state: { paymentProcessing: true } });
    } catch (outcomeError) {
      console.error('Failed to confirm checkout outcome:', outcomeError);
      navigate(buildCustomerRoute(storeId, 'my-appointments'), { state: { paymentProcessing: true } });
    }
  };

  const handleCheckoutClose = () => {
    if (checkoutFinishedRef.current) return;
    checkoutFinishedRef.current = true;
    const appointmentId = checkoutAppointmentId;
    setCheckoutAppointmentId(null);
    if (!appointmentId) return;

    setReleasingCheckout(true);
    abandonAppointmentCheckout(appointmentId)
      .then((result) => {
        if (result.outcome === 'Confirmed') {
          showBookingConfirmed();
          return;
        }
        if (result.outcome === 'Processing') {
          navigate(buildCustomerRoute(storeId, 'my-appointments'), { state: { paymentProcessing: true } });
          return;
        }
        setSuccessMessage(null);
        setError(t('customer.appointments.booking.paymentNotCompleted'));
      })
      .catch((abandonError) => {
        console.error('Failed to release unpaid booking:', abandonError);
        setError(t('customer.appointments.booking.paymentReleaseFailed'));
      })
      .finally(() => setReleasingCheckout(false));
  };

  const pageMaxWidth = 1120;
  const canProceedFromCalendar = useMemo(() => {
    if (!selectedDate) {
      return false;
    }

    if (professionalMode === 'perService') {
      return professionalScheduleGroups.length > 0 && professionalScheduleGroups.every((group) => Boolean(selectedGroupTimeSlots[group.cacheKey]));
    }

    if (!selectedTimeSlot) {
      return false;
    }

    if (professionalMode === 'any') {
      return Boolean(selectedTeamMemberIdForSlot) || selectedSlotRequiresConfirmation;
    }

    return true;
  }, [professionalMode, professionalScheduleGroups, selectedDate, selectedGroupTimeSlots, selectedSlotRequiresConfirmation, selectedTeamMemberIdForSlot, selectedTimeSlot]);
  const selectedProfessionalScheduleCount = professionalScheduleGroups.filter((group) => Boolean(selectedGroupTimeSlots[group.cacheKey])).length;
  const groupedTimelineColumnMinWidth = isMobileViewport
    ? (professionalScheduleColumns.length >= 4 ? 102 : professionalScheduleColumns.length === 3 ? 112 : 122)
    : professionalScheduleColumns.length >= 4 ? 140 : 156;
  const groupedTimelineHeaderPaddingX = isMobileViewport ? 0.75 : 1.25;
  const groupedTimelineHeaderPaddingY = isMobileViewport ? 1 : 1.25;
  const groupedTimelineCellPaddingX = isMobileViewport ? 0.25 : 0.5;
  const groupedTimelineCellPaddingY = isMobileViewport ? 0.25 : 0.35;
  const groupedTimelineSlotMinHeight = isMobileViewport ? 34 : 38;
  const groupedTimelineGridTemplateColumns = professionalScheduleColumns.length > 0
    ? `repeat(${professionalScheduleColumns.length}, minmax(${groupedTimelineColumnMinWidth}px, 1fr))`
    : 'minmax(0, 1fr)';
  const groupedTimelineMinWidth = professionalScheduleColumns.length > 0
    ? `${professionalScheduleColumns.length * groupedTimelineColumnMinWidth}px`
    : '100%';
  const stepLabels = [
    ...(!bookingViaMembership ? [t('customer.appointments.booking.stepServices')] : []),
    ...(!skipProfessionalsStep ? [t('customer.appointments.booking.stepProfessionals')] : []),
    t('customer.appointments.booking.stepCalendar'),
    t('customer.appointments.booking.stepReview'),
  ];
  const displayStepIndex = (() => {
    if (activeStep === STEP_SERVICES) return 0;
    if (activeStep === STEP_PROFESSIONALS) return bookingViaMembership ? 0 : 1;
    if (activeStep === STEP_CALENDAR) {
      return (bookingViaMembership ? 0 : 1) + (skipProfessionalsStep ? 0 : 1);
    }
    return stepLabels.length - 1;
  })();

  if (loadingData) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}>
        <CircularProgress sx={{ color: BRAND.accent }} />
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100%', bgcolor: BRAND.background, py: { xs: 2, sm: 4 }, px: 0, pb: activeStep === STEP_SERVICES && selectedServices.length > 0 ? 9 : undefined }}>
      <Box sx={{ width: '100%', maxWidth: pageMaxWidth, mx: 'auto' }}>
        <Typography variant="h5" sx={{ fontWeight: 600, color: BRAND.text, mb: 1 }}>
          {t('customer.appointments.title')}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          {stepLabels[Math.max(0, displayStepIndex)]}
        </Typography>

        <Box sx={{ display: 'flex', gap: 0.75, mb: 3 }}>
          {stepLabels.map((_, step) => (
            <Box
              key={step}
              sx={{
                flex: 1,
                height: 4,
                borderRadius: 2,
                bgcolor: step <= displayStepIndex ? BRAND.accent : brandAlpha('accent', 0.2),
                transition: 'background 0.2s ease',
              }}
            />
          ))}
        </Box>

        {error && (
          <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2, borderRadius: 2 }}>
            {error}
          </Alert>
        )}
        {successMessage && (
          <Alert severity="success" sx={{ mb: 2, borderRadius: 2 }}>
            {successMessage}
          </Alert>
        )}

        {activeStep === STEP_SERVICES && !isFreePlan && showMembershipDiscovery && (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mb: 2, textAlign: 'center', px: 1 }}
          >
            {t('customer.appointments.booking.discoverMembershipPrompt')}
            {' '}
            <Box
              component="button"
              type="button"
              onClick={() => navigate(buildCustomerRoute(storeId, 'memberships'))}
              sx={{
                display: 'inline',
                p: 0,
                m: 0,
                border: 'none',
                background: 'none',
                color: BRAND.accentText,
                font: 'inherit',
                fontWeight: 600,
                cursor: 'pointer',
                textDecoration: 'underline',
                textUnderlineOffset: '2px',
                ...hoverCapableStyles({ color: BRAND.accentDark }),
              }}
            >
              {t('customer.appointments.booking.seeMembershipPlans')}
            </Box>
          </Typography>
        )}

        {activeStep === STEP_SERVICES && !isFreePlan && bookingPlanCards.length > 0 && (
          <Paper elevation={0} sx={{ borderRadius: 2, border: `1px solid ${brandAlpha('accent', 0.2)}`, overflow: 'hidden', mb: 2 }}>
            <Box sx={{ p: { xs: 2, sm: 3 } }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, color: BRAND.text, mb: 2 }}>
                {activeMembershipPlans.length > 0
                  ? t('customer.appointments.booking.yourMemberships')
                  : t('customer.appointments.booking.selectedMembershipPlan')}
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: 'repeat(2, minmax(0, 1fr))' }, gap: 1.25 }}>
                {bookingPlanCards.map((plan) => {
                  const isSelected = selectedMembershipPlanId === plan.id;
                  const isActiveMembership = activeMembershipPlans.some((activePlan) => activePlan.id === plan.id);
                  const planName = getLocalizedValue(plan.name, lang, storeLanguage, t('customer.memberships.card.unnamed'));

                  return (
                    <Box
                      key={plan.id}
                      onClick={() => handleToggleMembershipPlan(plan)}
                      sx={{
                        display: 'flex',
                        flexDirection: { xs: 'column', sm: 'row' },
                        alignItems: { xs: 'flex-start', sm: 'center' },
                        justifyContent: 'space-between',
                        gap: 1.5,
                        p: 2,
                        borderRadius: 1.5,
                        border: `2px solid ${isSelected ? BRAND.accent : brandAlpha('accent', 0.35)}`,
                        bgcolor: isSelected ? brandAlpha('accent', 0.14) : brandAlpha('accent', 0.03),
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        ...hoverCapableStyles({ borderColor: BRAND.accent, bgcolor: isSelected ? brandAlpha('accent', 0.18) : brandAlpha('accent', 0.08) }),
                      }}
                    >
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="subtitle1" fontWeight={700} sx={{ color: BRAND.text }}>
                          {planName}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                          {t('customer.appointments.booking.membershipPlanServices', { count: plan.serviceIds.length })}
                        </Typography>
                        {isActiveMembership ? (
                          <Chip
                            size="small"
                            label={t('customer.appointments.booking.membershipPlanActive')}
                            sx={{ mt: 0.5, bgcolor: alpha('#2e7d32', 0.1), color: '#2e7d32', fontWeight: 600 }}
                          />
                        ) : (
                          <Typography variant="caption" sx={{ color: BRAND.textMuted, display: 'block', mt: 0.5 }}>
                            {t('customer.appointments.booking.membershipPlanNotEnrolled')}
                          </Typography>
                        )}
                      </Box>
                      <Typography variant="body1" fontWeight={700} sx={{ color: BRAND.accentText, flexShrink: 0 }}>
                        {isActiveMembership
                          ? t('customer.appointments.booking.membershipPlanCovered')
                          : formatCurrency(plan.price, currencyCode)}
                      </Typography>
                    </Box>
                  );
                })}
              </Box>
            </Box>
          </Paper>
        )}

        {activeStep === STEP_SERVICES && !isFreePlan && showPackageDiscovery && (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mb: 2, textAlign: 'center', px: 1 }}
          >
            {t('customer.appointments.booking.discoverPackagesPrompt')}
            {' '}
            <Box
              component="button"
              type="button"
              id="booking-see-packages"
              onClick={() => navigate(buildCustomerRoute(storeId, 'packages'))}
              sx={{
                display: 'inline',
                p: 0,
                m: 0,
                border: 'none',
                background: 'none',
                color: BRAND.accentText,
                font: 'inherit',
                fontWeight: 600,
                cursor: 'pointer',
                textDecoration: 'underline',
                textUnderlineOffset: '2px',
                ...hoverCapableStyles({ color: BRAND.accentDark }),
              }}
            >
              {t('customer.appointments.booking.seePackages')}
            </Box>
          </Typography>
        )}

        {activeStep === STEP_SERVICES && usableServicePackages.length > 0 && (
          <Paper elevation={0} sx={{ borderRadius: 2, border: `1px solid ${brandAlpha('accent', 0.2)}`, overflow: 'hidden', mb: 2 }}>
            <Box sx={{ p: { xs: 2, sm: 3 } }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, color: BRAND.text, mb: 2 }}>
                {t('customer.appointments.booking.yourPackages')}
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: 'repeat(2, minmax(0, 1fr))' }, gap: 1.25 }}>
                {usableServicePackages.map((servicePackage) => {
                  const isSelected = selectedServicePackageId === servicePackage.id;
                  const packageName = getLocalizedValue(servicePackage.name, lang, storeLanguage, t('customer.packages.card.unnamed'));
                  const packageServiceNames = servicePackage.includedServiceIds
                    .map((serviceId) => (services || []).find((service) => service.id === serviceId))
                    .filter(Boolean)
                    .map((service) => translateServiceName(service.name))
                    .join(' + ');

                  return (
                    <Box
                      key={servicePackage.id}
                      id={`booking-package-${servicePackage.id}`}
                      onClick={() => handleToggleServicePackage(servicePackage)}
                      sx={{
                        display: 'flex',
                        flexDirection: { xs: 'column', sm: 'row' },
                        alignItems: { xs: 'flex-start', sm: 'center' },
                        justifyContent: 'space-between',
                        gap: 1.5,
                        p: 2,
                        borderRadius: 1.5,
                        border: `2px solid ${isSelected ? BRAND.accent : brandAlpha('accent', 0.35)}`,
                        bgcolor: isSelected ? brandAlpha('accent', 0.14) : brandAlpha('accent', 0.03),
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        ...hoverCapableStyles({ borderColor: BRAND.accent, bgcolor: isSelected ? brandAlpha('accent', 0.18) : brandAlpha('accent', 0.08) }),
                      }}
                    >
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="subtitle1" fontWeight={700} sx={{ color: BRAND.text }}>
                          {packageName}
                        </Typography>
                        {packageServiceNames ? (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                            {packageServiceNames}
                          </Typography>
                        ) : null}
                        <Typography variant="caption" sx={{ color: BRAND.textMuted, display: 'block', mt: 0.5 }}>
                          {servicePackage.expiresOn
                            ? t('customer.packages.usage.validUntil', { date: formatCalendarDate(servicePackage.expiresOn) })
                            : t('customer.packages.usage.noExpiry')}
                        </Typography>
                      </Box>
                      <Chip
                        size="small"
                        label={t('customer.packages.usage.remaining', {
                          remaining: servicePackage.remainingQuantity,
                          total: servicePackage.quantity,
                        })}
                        sx={{ bgcolor: alpha('#2e7d32', 0.1), color: '#2e7d32', fontWeight: 700, flexShrink: 0 }}
                      />
                    </Box>
                  );
                })}
              </Box>
            </Box>
          </Paper>
        )}

        {activeStep === STEP_SERVICES && (
          showServiceCategories ? (
            <FolderTabs
              tabs={bookingCategoryTabs}
              value={serviceCategoryTab}
              onChange={setServiceCategoryTab}
              contentSx={{ border: `1px solid ${brandAlpha('accent', 0.2)}`, borderRadius: 2 }}
            >
              <Box sx={{ p: { xs: 2, sm: 3 } }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, color: BRAND.text, mb: 2 }}>
                  {t('customer.appointments.booking.selectServices')}
                </Typography>
                {entitlementLock && (
                  <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
                    {t(selectedServicePackage
                      ? 'customer.appointments.booking.packageExtrasHint'
                      : 'customer.appointments.booking.membershipExtrasHint')}
                  </Alert>
                )}
                {(!services || services.length === 0) ? (
                  <Typography variant="body2" color="text.secondary">{t('customer.store.noServices')}</Typography>
                ) : (
                  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: 'repeat(2, minmax(0, 1fr))' }, gap: 1.25 }}>
                    {displayedServices.map((service) => {
                      const selected = selectedServices.some((s) => s.id === service.id);
                      const isPlanService = Boolean(
                        entitlementLock?.serviceIds?.includes(service.id),
                      );
                      return (
                        <Box
                          key={service.id}
                          onClick={() => handleToggleService(service)}
                          sx={{
                            display: 'flex',
                            flexDirection: { xs: 'column', sm: 'row' },
                            alignItems: { xs: 'flex-start', sm: 'center' },
                            justifyContent: 'space-between',
                            gap: 1.5,
                            p: 2,
                            borderRadius: 1.5,
                            border: `2px solid ${selected ? BRAND.accent : brandAlpha('accent', 0.2)}`,
                            bgcolor: selected ? BRAND.accent : BRAND.surface,
                            cursor: isPlanService ? 'default' : 'pointer',
                            opacity: isPlanService ? 0.92 : 1,
                            transition: 'all 0.2s ease',
                            ...hoverCapableStyles({ borderColor: selected ? BRAND.accentDark : brandAlpha('accent', 0.65), bgcolor: selected ? BRAND.accentDark : brandAlpha('accent', 0.08) }),
                          }}
                        >
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography variant="subtitle1" fontWeight={600} sx={{ color: selected ? BRAND.onAccent : BRAND.text }}>
                              {translateServiceName(service.name)}
                            </Typography>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mt: 0.5 }}>
                              <Chip label={formatServiceDurationLabel(service, { onRequestLabel: t('servicesManagement.valueMode.onRequest') })} size="small" sx={{ bgcolor: selected ? brandAlpha('onAccent', 0.18) : brandAlpha('accent', 0.1), color: selected ? BRAND.onAccent : BRAND.accentText, fontWeight: 500 }} />
                              <Typography component="span" variant="body2" sx={{ fontWeight: 600, color: selected ? BRAND.onAccent : BRAND.accentText, alignSelf: 'center' }}>
                                {formatServicePriceLabel(service, {
                                  formatMoney: (amount) => formatCurrency(amount, currencyCode),
                                  onRequestLabel: t('servicesManagement.valueMode.onRequest'),
                                  freeLabel: t('customer.store.freePrice'),
                                  fromLabel: (price) => t('servicesManagement.valueMode.priceFrom', { price }),
                                })}
                              </Typography>
                            </Box>
                          </Box>
                        </Box>
                      );
                    })}
                  </Box>
                )}
                {hasInvalidSecondarySelection && (
                  <Alert severity="warning" sx={{ mt: 2, borderRadius: 2 }}>
                    {t('customer.appointments.booking.requiresCompanionService')}
                  </Alert>
                )}
              </Box>
            </FolderTabs>
          ) : (
          <Paper elevation={0} sx={{ borderRadius: 2, border: `1px solid ${brandAlpha('accent', 0.2)}`, overflow: 'hidden' }}>
            <Box sx={{ p: { xs: 2, sm: 3 } }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, color: BRAND.text, mb: 2 }}>
                {t('customer.appointments.booking.selectServices')}
              </Typography>
              {entitlementLock && (
                <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
                  {t(selectedServicePackage
                    ? 'customer.appointments.booking.packageExtrasHint'
                    : 'customer.appointments.booking.membershipExtrasHint')}
                </Alert>
              )}
              {(!services || services.length === 0) ? (
                <Typography variant="body2" color="text.secondary">{t('customer.store.noServices')}</Typography>
              ) : (
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: 'repeat(2, minmax(0, 1fr))' }, gap: 1.25 }}>
                  {displayedServices.map((service) => {
                    const selected = selectedServices.some((s) => s.id === service.id);
                    const isPlanService = Boolean(
                      entitlementLock?.serviceIds?.includes(service.id),
                    );
                    return (
                      <Box
                        key={service.id}
                        onClick={() => handleToggleService(service)}
                        sx={{
                          display: 'flex',
                          flexDirection: { xs: 'column', sm: 'row' },
                          alignItems: { xs: 'flex-start', sm: 'center' },
                          justifyContent: 'space-between',
                          gap: 1.5,
                          p: 2,
                          borderRadius: 1.5,
                          border: `2px solid ${selected ? BRAND.accent : brandAlpha('accent', 0.2)}`,
                          bgcolor: selected ? BRAND.accent : BRAND.surface,
                          cursor: isPlanService ? 'default' : 'pointer',
                          opacity: isPlanService ? 0.92 : 1,
                          transition: 'all 0.2s ease',
                          ...hoverCapableStyles({ borderColor: selected ? BRAND.accentDark : brandAlpha('accent', 0.65), bgcolor: selected ? BRAND.accentDark : brandAlpha('accent', 0.08) }),
                        }}
                      >
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography variant="subtitle1" fontWeight={600} sx={{ color: selected ? BRAND.onAccent : BRAND.text }}>
                            {translateServiceName(service.name)}
                          </Typography>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mt: 0.5 }}>
                            <Chip label={formatServiceDurationLabel(service, { onRequestLabel: t('servicesManagement.valueMode.onRequest') })} size="small" sx={{ bgcolor: selected ? brandAlpha('onAccent', 0.18) : brandAlpha('accent', 0.1), color: selected ? BRAND.onAccent : BRAND.accentText, fontWeight: 500 }} />
                            <Typography component="span" variant="body2" sx={{ fontWeight: 600, color: selected ? BRAND.onAccent : BRAND.accentText, alignSelf: 'center' }}>
                              {formatServicePriceLabel(service, {
                                formatMoney: (amount) => formatCurrency(amount, currencyCode),
                                onRequestLabel: t('servicesManagement.valueMode.onRequest'),
                                freeLabel: t('customer.store.freePrice'),
                                fromLabel: (price) => t('servicesManagement.valueMode.priceFrom', { price }),
                              })}
                            </Typography>
                          </Box>
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              )}
              {hasInvalidSecondarySelection && (
                <Alert severity="warning" sx={{ mt: 2, borderRadius: 2 }}>
                  {t('customer.appointments.booking.requiresCompanionService')}
                </Alert>
              )}
            </Box>
          </Paper>
          )
        )}

      {activeStep === STEP_PROFESSIONALS && (
        <Paper elevation={0} sx={{ borderRadius: 2, border: `1px solid ${brandAlpha('accent', 0.2)}`, overflow: 'hidden' }}>
          <Box sx={{ p: { xs: 2, sm: 3 } }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, color: BRAND.text, mb: 2 }}>
              {t('customer.appointments.booking.stepProfessionals')}
            </Typography>
            <RadioGroup value={professionalMode} onChange={handleProfessionalModeChange} sx={{ gap: 1 }}>
              {[
                { value: 'any', label: t('customer.appointments.booking.anyProfessional') },
                { value: 'specific', label: t('customer.appointments.booking.specificProfessional'), disabled: specificOptionDisabled },
                { value: 'perService', label: t('customer.appointments.booking.professionalPerService') },
              ].map((opt) => (
                <Paper
                  key={opt.value}
                  elevation={0}
                  component="label"
                  sx={{
                    p: 2,
                    borderRadius: 1.5,
                    border: `1px solid ${professionalMode === opt.value ? BRAND.accent : brandAlpha('accent', 0.2)}`,
                    bgcolor: professionalMode === opt.value ? brandAlpha('accent', 0.06) : BRAND.onAccent,
                    cursor: opt.disabled ? 'not-allowed' : 'pointer',
                    opacity: opt.disabled ? 0.55 : 1,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    transition: 'all 0.2s ease',
                    ...hoverCapableStyles({ borderColor: opt.disabled ? brandAlpha('accent', 0.2) : brandAlpha('accent', 0.4) }),
                  }}
                >
                  <Radio value={opt.value} disabled={opt.disabled} sx={{ color: BRAND.accentText, '&.Mui-checked': { color: BRAND.accentText } }} />
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>{opt.label}</Typography>
                </Paper>
              ))}
            </RadioGroup>

            {specificOptionDisabled && (
              <Alert severity="info" sx={{ mt: 2, borderRadius: 2 }}>
                {t('customer.appointments.booking.specificProfessionalUnavailable')}
              </Alert>
            )}

            {hasInvalidSecondarySelection && (
              <Alert severity="warning" sx={{ mt: 2, borderRadius: 2 }}>
                {t('customer.appointments.booking.requiresCompanionService')}
              </Alert>
            )}

            {professionalMode === 'specific' && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="body2" fontWeight={500} sx={{ mb: 1 }}>{t('customer.appointments.booking.selectProfessional')}</Typography>
                <Select fullWidth value={specificTeamMemberId} onChange={(e) => setSpecificTeamMemberId(e.target.value)} displayEmpty size="small" sx={{ borderRadius: 1.5 }}>
                  <MenuItem value="">—</MenuItem>
                  {canDoAll.map((m) => (<MenuItem key={m.id} value={m.id}>{m.name}</MenuItem>))}
                </Select>
                <FormHelperText sx={{ mt: 0.5 }}>
                  {t('customer.appointments.booking.specificProfessionalHelper')}
                </FormHelperText>
              </Box>
            )}

            {professionalMode === 'perService' && (
              <Box sx={{ mt: 2 }}>
                {canDoAll.length > 0 && <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>{t('customer.appointments.booking.suggestionSingle')}</Typography>}
                {orderedSelectedServices.map((service) => {
                  const professionalOptions = getProfessionalOptionsForService(service);
                  const isRestrictedSecondary = isNonStandaloneSecondaryBookingService(service);
                  const helperText = isRestrictedSecondary
                    ? professionalOptions.length > 0
                      ? t('customer.appointments.booking.companionProfessionalOnly')
                      : t('customer.appointments.booking.selectCompanionProfessionalFirst')
                    : '';

                  return (
                  <Box key={service.id} sx={{ mb: 2 }}>
                    <Typography variant="body2" fontWeight={500} sx={{ mb: 0.5 }}>{t('customer.appointments.booking.selectProfessionalForService', { service: translateServiceName(service.name) })}</Typography>
                    <Select fullWidth size="small" value={perServiceAssignments[service.id] || ''} onChange={(e) => setPerServiceAssignments((prev) => ({ ...prev, [service.id]: e.target.value }))} displayEmpty sx={{ borderRadius: 1.5 }}>
                      <MenuItem value="">—</MenuItem>
                      {professionalOptions.map((teamMember) => (<MenuItem key={teamMember.id} value={teamMember.id}>{teamMember.name}</MenuItem>))}
                    </Select>
                    {helperText && (
                      <FormHelperText sx={{ mt: 0.5 }}>
                        {helperText}
                      </FormHelperText>
                    )}
                  </Box>
                  );
                })}
              </Box>
            )}

            <Box sx={{ mt: 3, display: 'flex', justifyContent: 'space-between' }}>
              <Button onClick={handleBackFromProfessionals} sx={{ color: BRAND.accentText, fontWeight: 600 }}>{t('customer.appointments.booking.back')}</Button>
              <Button variant="contained" onClick={() => setActiveStep(STEP_CALENDAR)} disabled={!canProceedFromProfessionals} sx={{ bgcolor: BRAND.accent, color: BRAND.onAccent, px: 3, ...hoverCapableStyles({ bgcolor: BRAND.accentDark }) }}>
                {t('customer.appointments.booking.next')}
              </Button>
            </Box>
          </Box>
        </Paper>
      )}

      {activeStep === STEP_CALENDAR && (
        <Paper elevation={0} sx={{ borderRadius: 2, border: `1px solid ${brandAlpha('accent', 0.2)}`, overflow: 'hidden' }}>
          <Box sx={{ p: { xs: 2, sm: 3 } }}>
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', lg: showGroupedProfessionalSchedules ? 'column' : 'row' }, alignItems: 'stretch' }}>
              <Box sx={{ flex: { xs: 'none', lg: showGroupedProfessionalSchedules ? '1 1 auto' : '0 0 60%' }, maxWidth: { lg: showGroupedProfessionalSchedules ? '100%' : '60%' }, minWidth: 0 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, color: BRAND.text, mb: 0.5 }}>{t('customer.appointments.booking.selectTime')}</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontWeight: 500, textAlign: 'center' }}>
                  {visibleStripDates[0] ? new Date(visibleStripDates[0] + 'T12:00:00').toLocaleDateString(lang, { month: 'long' }) : new Date().toLocaleDateString(lang, { month: 'long' })}
                </Typography>
                <Box ref={dateStripRowRef} sx={{ display: 'flex', alignItems: 'center', gap: 0.25, mb: 2, flexWrap: 'nowrap', width: '100%', minWidth: 0 }}>
                  <IconButton
                    size="small"
                    onClick={() => setStripPage((p) => Math.max(0, p - 1))}
                    disabled={stripPage === 0}
                    sx={{ color: BRAND.accentText, '&.Mui-disabled': { opacity: 0.4 }, flexShrink: 0 }}
                    aria-label={t('customer.appointments.booking.selectDate')}
                  >
                    <ChevronLeftIcon />
                  </IconButton>
                  <Box sx={{ display: 'flex', gap: 1, overflowX: 'auto', py: 0.5, flexWrap: 'nowrap', flex: '1 1 0', minWidth: 0, justifyContent: { xs: 'flex-start', sm: 'center' }, scrollSnapType: 'x proximity' }}>
                    {visibleStripDates.map((date) => {
                      const isSelected = selectedDate === date;
                      const d = new Date(date + 'T12:00:00');
                      const dayNum = d.getDate();
                      const weekday = d.toLocaleDateString(lang, { weekday: 'short' });
                      const hasFetched = date in availabilityByDate;
                      const hasSlots = availabilityByDate[date]?.length > 0;
                      const isClosedDay = isClosedOnDate(date, teamMemberIdsForApi);
                      const isDisabled = showGroupedProfessionalSchedules
                        ? isClosedDay
                        : (isClosedDay || (hasFetched && !hasSlots));
                      return (
                        <Box
                          key={date}
                          onClick={() => !isDisabled && handleDateSelect(date)}
                          sx={{
                            minWidth: 52,
                            flexShrink: 0,
                            py: 1.5,
                            px: 1,
                            borderRadius: 1.5,
                            border: `1px solid ${isSelected ? BRAND.accent : brandAlpha('accent', 0.2)}`,
                            bgcolor: isSelected ? BRAND.accent : BRAND.surface,
                            cursor: isDisabled ? 'default' : 'pointer',
                            opacity: isDisabled ? 0.5 : 1,
                            textAlign: 'center',
                            scrollSnapAlign: 'start',
                            transition: 'all 0.2s ease',
                            ...(isDisabled ? {} : hoverCapableStyles({ borderColor: isSelected ? BRAND.accentDark : BRAND.accent, bgcolor: isSelected ? BRAND.accentDark : brandAlpha('accent', 0.08) })),
                          }}
                        >
                          <Typography variant="body2" fontWeight={700} sx={{ lineHeight: 1.2, color: isSelected ? BRAND.onAccent : BRAND.text }}>{dayNum}</Typography>
                          <Typography variant="caption" sx={{ display: 'block', color: isSelected ? brandAlpha('onAccent', 0.8) : 'text.secondary' }}>{weekday}</Typography>
                        </Box>
                      );
                    })}
                  </Box>
                  <IconButton
                    size="small"
                    onClick={() => setStripPage((p) => Math.min(maxStripPage, p + 1))}
                    disabled={stripPage >= maxStripPage}
                    sx={{ color: BRAND.accentText, '&.Mui-disabled': { opacity: 0.4 }, flexShrink: 0 }}
                    aria-label={t('customer.appointments.booking.selectDate')}
                  >
                    <ChevronRightIcon />
                  </IconButton>
                </Box>
                {loadingAvailability ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress sx={{ color: BRAND.accentText }} size={32} /></Box>
                ) : showGroupedProfessionalSchedules ? (
                  <Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                      {selectedDate && new Date(selectedDate + 'T12:00:00').toLocaleDateString(lang, { weekday: 'long', day: 'numeric', month: 'short' })}
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'flex-start', sm: 'center' }, justifyContent: 'space-between', gap: 1, mb: 1.5 }}>
                      <Typography variant="body2" color="text.secondary">
                        {t('customer.appointments.booking.selectTimeMobileHint')}
                      </Typography>
                      <Chip
                        label={t('customer.appointments.booking.professionalsReady', { selected: selectedProfessionalScheduleCount, total: professionalScheduleColumns.length })}
                        size="small"
                        sx={{ bgcolor: brandAlpha('accent', 0.12), color: BRAND.accentText, fontWeight: 700 }}
                      />
                    </Box>
                    {groupedTimelineTimeSlots.length === 0 ? (
                      <Typography variant="body2" color="text.secondary">
                        {t('customer.appointments.booking.noSlots')}
                      </Typography>
                    ) : (
                      <Box
                        sx={{
                          overflow: 'auto',
                          maxHeight: { xs: 380, sm: 430, lg: 520 },
                          borderRadius: 2,
                          border: `1px solid ${brandAlpha('accent', 0.22)}`,
                          bgcolor: BRAND.surface,
                          WebkitOverflowScrolling: 'touch',
                        }}
                      >
                        <Box
                          sx={{
                            display: 'grid',
                            gridTemplateColumns: groupedTimelineGridTemplateColumns,
                            minWidth: groupedTimelineMinWidth,
                          }}
                        >
                          {professionalScheduleColumns.map((column, index) => {
                            const constraint = groupedScheduleConstraints[column.cacheKey];
                            const selectedGroupSlot = selectedGroupTimeSlots[column.cacheKey];
                            const helperText = column.slotTimes.length === 0
                              ? t('customer.appointments.booking.noSlotsForProfessional')
                              : constraint?.isLocked
                                ? t('customer.appointments.booking.selectPreviousTimeFirst')
                                : constraint?.minimumStartMinute != null
                                  ? t('customer.appointments.booking.availableFrom', { time: formatMinutesToTime(constraint.minimumStartMinute) })
                                  : t('customer.appointments.booking.pickDate');

                            return (
                              <Box
                                key={`group-header-${column.cacheKey}`}
                                sx={{
                                  position: 'sticky',
                                  top: 0,
                                  zIndex: 1,
                                  px: groupedTimelineHeaderPaddingX,
                                  py: groupedTimelineHeaderPaddingY,
                                  minWidth: 0,
                                  bgcolor: BRAND.background,
                                  borderBottom: `1px solid ${brandAlpha('accent', 0.2)}`,
                                  borderRight: index === professionalScheduleColumns.length - 1 ? 'none' : `1px solid ${brandAlpha('accent', 0.12)}`,
                                }}
                              >
                                <Typography
                                  variant="subtitle2"
                                  sx={{
                                    fontWeight: 700,
                                    color: BRAND.text,
                                    lineHeight: 1.15,
                                    mb: 0.25,
                                    fontSize: isMobileViewport ? '0.8rem' : undefined,
                                    display: '-webkit-box',
                                    WebkitLineClamp: 2,
                                    WebkitBoxOrient: 'vertical',
                                    overflow: 'hidden',
                                    minHeight: isMobileViewport ? 30 : 34,
                                  }}
                                >
                                  {column.teamMemberName || t('customer.appointments.booking.professional')}
                                </Typography>
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  sx={{
                                    display: '-webkit-box',
                                    lineHeight: 1.25,
                                    minHeight: isMobileViewport ? 28 : 34,
                                    fontSize: isMobileViewport ? '0.68rem' : undefined,
                                    WebkitLineClamp: 2,
                                    WebkitBoxOrient: 'vertical',
                                    overflow: 'hidden',
                                  }}
                                >
                                  {column.serviceSummary}
                                </Typography>
                                <Box sx={{ mt: isMobileViewport ? 0.5 : 0.75, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 0.35 }}>
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      color: constraint?.isLocked ? 'text.secondary' : BRAND.accentText,
                                      fontWeight: 600,
                                      fontSize: isMobileViewport ? '0.68rem' : undefined,
                                      lineHeight: 1.15,
                                      pr: 0.25,
                                    }}
                                  >
                                    {helperText}
                                  </Typography>
                                  {selectedGroupSlot && (
                                    <Chip
                                      label={selectedGroupSlot}
                                      size="small"
                                      sx={{
                                        bgcolor: brandAlpha('accent', 0.12),
                                        color: BRAND.accentText,
                                        fontWeight: 700,
                                        maxWidth: isMobileViewport ? 58 : 72,
                                        height: isMobileViewport ? 22 : 24,
                                        '& .MuiChip-label': {
                                          px: isMobileViewport ? 0.75 : 1,
                                          fontSize: isMobileViewport ? '0.68rem' : undefined,
                                        },
                                      }}
                                    />
                                  )}
                                </Box>
                              </Box>
                            );
                          })}

                          {groupedTimelineTimeSlots.flatMap((timeStr) => professionalScheduleColumns.map((column, index) => {
                            const constraint = groupedScheduleConstraints[column.cacheKey];
                            const slotMinute = parseTimeToMinutes(timeStr);
                            const isAvailable = column.slotTimesSet.has(timeStr);
                            const isBeforeMinimum = constraint?.minimumStartMinute != null && slotMinute < constraint.minimumStartMinute;
                            const isDisabled = constraint?.isLocked || !isAvailable || isBeforeMinimum;
                            const isSelected = selectedGroupTimeSlots[column.cacheKey] === timeStr;

                            return (
                              <Box
                                key={`${column.cacheKey}-${timeStr}`}
                                sx={{
                                  px: groupedTimelineCellPaddingX,
                                  py: groupedTimelineCellPaddingY,
                                  borderBottom: `1px solid ${brandAlpha('accent', 0.08)}`,
                                  borderRight: index === professionalScheduleColumns.length - 1 ? 'none' : `1px solid ${brandAlpha('accent', 0.08)}`,
                                }}
                              >
                                <Button
                                  fullWidth
                                  variant={isSelected ? 'contained' : 'text'}
                                  size="small"
                                  disabled={isDisabled}
                                  onClick={() => handleGroupedTimeSlotSelect(column.cacheKey, timeStr)}
                                  sx={{
                                    position: 'relative',
                                    minHeight: groupedTimelineSlotMinHeight,
                                    borderRadius: 1.25,
                                    color: isSelected ? BRAND.onAccent : BRAND.accentText,
                                    bgcolor: isSelected ? BRAND.accent : 'transparent',
                                    fontWeight: isSelected ? 700 : 600,
                                    fontSize: isMobileViewport ? '0.78rem' : undefined,
                                    px: isMobileViewport ? 0.25 : 0.75,
                                    opacity: isAvailable ? 1 : 0.28,
                                    ...hoverCapableStyles({
                                      bgcolor: isSelected ? BRAND.accentDark : brandAlpha('accent', 0.08),
                                    }),
                                    '&.Mui-disabled': {
                                      color: isAvailable ? brandAlpha('text', isBeforeMinimum ? 0.5 : 0.34) : brandAlpha('text', 0.22),
                                      bgcolor: isAvailable && isBeforeMinimum ? brandAlpha('accent', 0.06) : 'transparent',
                                    },
                                  }}
                                >
                                  <Box component="span">{timeStr}</Box>
                                  <BookingTimeSlotCoins
                                    hasDifferentialPricing={hasDifferentialPricing}
                                    hasAdditionalCost={Boolean(
                                      (groupAvailabilityByDate[column.cacheKey]?.[selectedDate] || [])
                                        .find((slot) => (typeof slot.timeSlot === 'string' ? slot.timeSlot : String(slot.timeSlot ?? '')) === timeStr)
                                        ?.hasAdditionalCost,
                                    )}
                                    currencyCode={currencyCode}
                                    selected={isSelected}
                                  />
                                </Button>
                              </Box>
                            );
                          }))}
                        </Box>
                      </Box>
                    )}
                  </Box>
                ) : selectedDate && Array.isArray(availabilityByDate[selectedDate]) ? (
                  <Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>{new Date(selectedDate + 'T12:00:00').toLocaleDateString(lang, { weekday: 'long', day: 'numeric', month: 'short' })}</Typography>
                    {availabilityByDate[selectedDate].length === 0 ? (
                      <Typography variant="body2" color="text.secondary">{t('customer.appointments.booking.noSlots')}</Typography>
                    ) : (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, maxHeight: 280, overflowY: 'auto', pr: 0.5 }}>
                        {availabilityByDate[selectedDate].map((slot) => {
                          const timeStr = typeof slot.timeSlot === 'string' ? slot.timeSlot : String(slot.timeSlot ?? '');
                          const availableIds = slot.availableTeamMemberIds || [];
                          const requiresConfirmation = Boolean(slot.requiresConfirmation);
                          const isSelected = selectedTimeSlot === timeStr && (
                            professionalMode !== 'any' || selectedTeamMemberIdForSlot || requiresConfirmation
                          );
                          return (
                            <Button
                              key={timeStr}
                              variant={isSelected ? 'contained' : 'outlined'}
                              fullWidth
                              size="medium"
                              onClick={() => handleTimeSlotSelect(selectedDate, timeStr, availableIds, requiresConfirmation)}
                              sx={{
                                position: 'relative',
                                justifyContent: 'center',
                                minHeight: 44,
                                borderColor: brandAlpha('accent', 0.5),
                                color: isSelected ? BRAND.onAccent : BRAND.accentText,
                                bgcolor: isSelected ? BRAND.accent : 'transparent',
                                fontWeight: 600,
                                ...hoverCapableStyles({
                                  borderColor: BRAND.accent,
                                  bgcolor: isSelected ? BRAND.accentDark : brandAlpha('accent', 0.08),
                                  color: isSelected ? BRAND.onAccent : BRAND.accentText,
                                }),
                              }}
                            >
                              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', lineHeight: 1.2 }}>
                                <Box component="span">{timeStr}</Box>
                                {requiresConfirmation && (
                                  <Box
                                    component="span"
                                    sx={{
                                      mt: 0.25,
                                      fontSize: '0.7rem',
                                      fontWeight: 600,
                                      opacity: isSelected ? 0.92 : 0.8,
                                    }}
                                  >
                                    {t('customer.appointments.booking.onRequestSlot')}
                                  </Box>
                                )}
                              </Box>
                              <BookingTimeSlotCoins
                                hasDifferentialPricing={hasDifferentialPricing}
                                hasAdditionalCost={Boolean(slot.hasAdditionalCost)}
                                currencyCode={currencyCode}
                                selected={isSelected}
                              />
                            </Button>
                          );
                        })}
                      </Box>
                    )}
                  </Box>
                ) : null}
              </Box>
              <Box sx={{ display: { xs: 'none', lg: showGroupedProfessionalSchedules ? 'none' : 'block' }, flex: '0 0 10%', minWidth: 0 }} aria-hidden />
              <Box sx={{ display: { xs: 'none', lg: showGroupedProfessionalSchedules ? 'none' : 'block' }, flex: '0 0 30%', maxWidth: '30%', minWidth: 0 }}>
                <Paper variant="outlined" sx={{ p: 2, height: '100%', minHeight: { lg: 280 }, borderRadius: 1.5, borderColor: brandAlpha('accent', 0.3), bgcolor: brandAlpha('accent', 0.03) }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, color: BRAND.text, mb: 1.5 }}>{t('customer.appointments.booking.summary')}</Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    <Box>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{t('customer.appointments.booking.services')}</Typography>
                      <Typography variant="body2" sx={{ color: BRAND.text }}>{orderedSelectedServices.length ? orderedSelectedServices.map((service) => translateServiceName(service.name)).join(', ') : '—'}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{t('customer.appointments.booking.professional')}</Typography>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                        {getAssignmentSummaryItems().map((item) => (
                          <Typography key={item} variant="body2" sx={{ color: BRAND.text }}>
                            {item}
                          </Typography>
                        ))}
                      </Box>
                    </Box>
                    {selectedDate && (
                      <Box>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{t('customer.appointments.booking.dateTime')}</Typography>
                        <Typography variant="body2" sx={{ color: BRAND.text }}>
                          {new Date(selectedDate + 'T12:00:00').toLocaleDateString(lang, { weekday: 'short', day: 'numeric', month: 'short' })}
                          {selectedTimeSlot ? ` · ${selectedTimeSlot}` : ''}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </Paper>
              </Box>
            </Box>
            {!isFreePlan && <BookingWaitlistCta onJoin={() => setWaitlistOpen(true)} />}
            <Box sx={{ mt: 3, display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', gap: 1 }}>
              <Button onClick={goBackFromCalendar} sx={{ color: BRAND.accentText, fontWeight: 600, width: { xs: '100%', sm: 'auto' } }}>{t('customer.appointments.booking.back')}</Button>
              <Button
                variant="contained"
                onClick={() => {
                  if (selectedPlanMembership && promptRenewalWarningIfNeeded('review')) {
                    return;
                  }
                  goToReviewStep();
                }}
                disabled={!canProceedFromCalendar}
                sx={{ bgcolor: BRAND.accent, color: BRAND.onAccent, px: 3, width: { xs: '100%', sm: 'auto' }, ...hoverCapableStyles({ bgcolor: BRAND.accentDark }) }}
              >
                {t('customer.appointments.booking.next')}
              </Button>
            </Box>
          </Box>
        </Paper>
      )}

      {activeStep === STEP_REVIEW && (
        <Paper elevation={0} sx={{ borderRadius: 2, border: `1px solid ${brandAlpha('accent', 0.2)}`, overflow: 'hidden' }}>
          <Box sx={{ p: { xs: 2, sm: 3 } }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, color: BRAND.text, mb: 2 }}>{t('customer.appointments.booking.summary')}</Typography>
            <Box sx={{ bgcolor: brandAlpha('accent', 0.05), borderRadius: 1.5, p: 2, mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 0.5 }}>
                <Typography variant="body2" color="text.secondary">{t('customer.appointments.booking.dateTime')}</Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.5 }}>
                  {showGroupedProfessionalSchedules ? professionalScheduleGroups.map((group) => (
                    <Typography key={`review-time-${group.cacheKey}`} variant="body2" fontWeight={600} sx={{ color: BRAND.text, textAlign: 'right' }}>
                      {`${group.teamMemberName}: ${selectedGroupTimeSlots[group.cacheKey] || '—'} · ${group.services.map((service) => translateServiceName(service.name)).join(', ')}`}
                    </Typography>
                  )) : (
                    <Typography variant="body2" fontWeight={600} sx={{ color: BRAND.text }}>
                      {selectedDate && new Date(selectedDate + 'T12:00:00').toLocaleDateString(lang, { weekday: 'short', day: 'numeric', month: 'short' })}
                      {selectedTimeSlot && ` · ${selectedTimeSlot}`}
                    </Typography>
                  )}
                </Box>
              </Box>
              <Divider sx={{ my: 1.5 }} />
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 0.5 }}>
                <Typography variant="body2" color="text.secondary">{t('customer.appointments.booking.services')}</Typography>
                <Typography variant="body2" fontWeight={600} sx={{ color: BRAND.text }}>{orderedSelectedServices.map((service) => translateServiceName(service.name)).join(', ')}</Typography>
              </Box>
              <Divider sx={{ my: 1.5 }} />
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 0.5 }}>
                <Typography variant="body2" color="text.secondary">{t('customer.appointments.booking.professional')}</Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.5 }}>
                    {getAssignmentSummaryItems().map((item) => (
                      <Typography key={item} variant="body2" fontWeight={600} sx={{ color: BRAND.text }}>
                        {item}
                      </Typography>
                    ))}
                    {selectedSlotRequiresConfirmation && professionalMode === 'any' && !selectedTeamMemberIdForSlot && (
                      <Typography variant="caption" color="text.secondary">
                        {t('customer.appointments.booking.onRequestAnyProfessionalHint')}
                      </Typography>
                    )}
                </Box>
              </Box>
              <Divider sx={{ my: 1.5 }} />
              {selectedSlotAdditionalCost > 0 && (
                <>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 0.5 }}>
                    <Typography variant="body2" color="text.secondary">{t('customer.appointments.booking.additionalCost')}</Typography>
                    <Typography variant="body2" fontWeight={600} sx={{ color: BRAND.text }}>
                      {formatCurrency(selectedSlotAdditionalCost, currencyCode)}
                    </Typography>
                  </Box>
                  <Divider sx={{ my: 1.5 }} />
                </>
              )}
              {!isFreePlan && catalogProducts.length > 0 ? (
                <>
                  <Box sx={{ display: 'flex', alignItems: 'stretch', justifyContent: 'space-between', gap: 1.5, minWidth: 0 }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between', gap: 0.75, flexShrink: 0 }}>
                      <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                        {t('customer.appointments.booking.products')}
                      </Typography>
                      <Button
                        onClick={openProductDialog}
                        color="inherit"
                        sx={{
                          color: BRAND.accentText,
                          fontWeight: 600,
                          px: 0,
                          minWidth: 0,
                          minHeight: 0,
                          py: 0,
                          mt: 'auto',
                          alignSelf: 'flex-start',
                          textTransform: 'none',
                          fontSize: '0.875rem',
                          lineHeight: 1.4,
                          whiteSpace: 'nowrap',
                          '&:hover': {
                            textDecoration: 'underline',
                            backgroundColor: 'transparent',
                          },
                        }}
                      >
                        {t('customer.appointments.booking.editProducts')}
                      </Button>
                    </Box>
                    <Box sx={{ textAlign: 'right', minWidth: 0, flex: '1 1 auto', overflow: 'hidden' }}>
                      <Typography
                        variant="body2"
                        fontWeight={600}
                        title={productLines.length > 0
                          ? formatProductLinesSummary(catalogProducts, productLines, lang)
                          : undefined}
                        sx={{
                          color: BRAND.text,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {productLines.length > 0
                          ? formatProductLinesSummary(catalogProducts, productLines, lang)
                          : t('customer.appointments.booking.noProductsSelected')}
                      </Typography>
                      {productLines.length > 0 ? (
                        <Typography variant="body2" fontWeight={700} sx={{ color: BRAND.accentText }}>
                          {formatCurrency(getProductLinesTotal(catalogProducts, productLines), currencyCode)}
                        </Typography>
                      ) : null}
                    </Box>
                  </Box>
                  <Divider sx={{ my: 1.5 }} />
                </>
              ) : null}
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 0.5 }}>
                <Typography variant="body2" color="text.secondary">
                  {selectedMembershipPlanId && !selectedPlanMembership
                    ? t('customer.appointments.booking.dueInStore')
                    : t('customer.appointments.booking.totalPrice')}
                </Typography>
                <Typography variant="body2" fontWeight={700} sx={{ color: BRAND.accentText }}>
                  {hasUnpricedSelection && bookingChargesAmount <= 0
                    ? t('customer.appointments.booking.priceToConfirm')
                    : formatCustomerPrice(calculateBookingReviewAmountDue({
                    selectedServices: orderedSelectedServices,
                    selectedMembershipPlanId,
                    selectedPlan,
                    selectedPlanMembership,
                    selectedMembershipId,
                    bookingEntitlements,
                    selectedServicePackageId: selectedPackagePaymentId,
                    packageEntitlements,
                    additionalCostAmount: selectedSlotAdditionalCost,
                    productsTotal: getProductLinesTotal(catalogProducts, productLines),
                  }))}
                </Typography>
              </Box>
              {!nothingToPay ? (
              <>
              <Divider sx={{ my: 1.5 }} />
              {selectedMembershipPlanId ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 0.5 }}>
                    <Typography variant="body2" color="text.secondary">{t('customer.appointments.booking.paymentMethod')}</Typography>
                    <Typography variant="body2" fontWeight={600} sx={{ color: BRAND.text }}>
                      {selectedPlanMembership
                        ? t('customer.appointments.booking.coveredByMembership')
                        : t('customer.appointments.booking.payInStore')}
                    </Typography>
                  </Box>
                  {!selectedPlanMembership && selectedPlan && (
                    <Alert severity="info" sx={{ mt: 0.5 }}>
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        {t('customer.appointments.booking.enrollInStoreNote', {
                          price: formatCurrency(selectedPlan.price, currencyCode),
                        })}
                      </Typography>
                      <Typography variant="body2">
                        {selectedPlan.entitlementType === 'UnlimitedUse'
                          ? t('customer.appointments.booking.planHowItWorksUnlimited')
                          : t('customer.appointments.booking.planHowItWorksAllowance', {
                              count: selectedPlan.allowanceQuantity ?? 0,
                            })}
                      </Typography>
                    </Alert>
                  )}
                </Box>
              ) : (
                <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 0.5 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: showAppointmentPaymentChoice ? 0.5 : 0 }}>{t('customer.appointments.booking.paymentMethod')}</Typography>
                  {showAppointmentPaymentChoice ? (
                    <RadioGroup
                      value={selectedPaymentMethod}
                      onChange={(e) => setSelectedPaymentMethod(e.target.value)}
                    >
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.25 }}>
                        {eligiblePackageEntitlements.map((entitlement) => {
                          const packagePaymentValue = `${PACKAGE_PAYMENT_PREFIX}${entitlement.customerServicePackageId}`;
                          const packageName = getLocalizedValue(entitlement.name, lang, storeLanguage, t('customer.packages.card.unnamed'));

                          return (
                            <Box
                              key={entitlement.customerServicePackageId}
                              id={`booking-pay-with-package-${entitlement.customerServicePackageId}`}
                              sx={{ display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'pointer' }}
                              onClick={() => setSelectedPaymentMethod(packagePaymentValue)}
                            >
                              <Radio value={packagePaymentValue} size="small" sx={{ color: BRAND.accentText, '&.Mui-checked': { color: BRAND.accentText }, p: 0.5 }} />
                              <Typography variant="body2" fontWeight={selectedPaymentMethod === packagePaymentValue ? 600 : 400} sx={{ color: BRAND.text, textAlign: 'right' }}>
                                {t('customer.appointments.booking.payWithPackage', {
                                  package: packageName,
                                  remaining: entitlement.remainingQuantity,
                                })}
                              </Typography>
                            </Box>
                          );
                        })}
                        {eligibleMembershipEntitlements.map((entitlement) => {
                          const membershipPaymentValue = `membership:${entitlement.customerMembershipId}`;
                          const planName = getLocalizedValue(entitlement.planName, lang, storeLanguage, t('customer.memberships.card.unnamed'));
                          const membershipLabel = entitlement.entitlementType === 'UnlimitedUse'
                            ? t('customer.appointments.booking.payWithMembershipUnlimited', { plan: planName })
                            : t('customer.appointments.booking.payWithMembershipAllowance', {
                                plan: planName,
                                remaining: entitlement.remainingQuantity ?? 0,
                              });

                          return (
                            <Box
                              key={entitlement.customerMembershipId}
                              sx={{ display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'pointer' }}
                              onClick={() => setSelectedPaymentMethod(membershipPaymentValue)}
                            >
                              <Radio value={membershipPaymentValue} size="small" sx={{ color: BRAND.accentText, '&.Mui-checked': { color: BRAND.accentText }, p: 0.5 }} />
                              <Typography variant="body2" fontWeight={selectedPaymentMethod === membershipPaymentValue ? 600 : 400} sx={{ color: BRAND.text, textAlign: 'right' }}>
                                {membershipLabel}
                              </Typography>
                            </Box>
                          );
                        })}
                        {appointmentAllowsOnline ? (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'pointer' }} onClick={() => setSelectedPaymentMethod('online')}>
                            <Radio value="online" size="small" sx={{ color: BRAND.accentText, '&.Mui-checked': { color: BRAND.accentText }, p: 0.5 }} />
                            <Typography variant="body2" fontWeight={selectedPaymentMethod === 'online' ? 600 : 400} sx={{ color: BRAND.text }}>{t('customer.appointments.booking.payOnline')}</Typography>
                          </Box>
                        ) : null}
                        {appointmentAllowsInStore ? (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'pointer' }} onClick={() => setSelectedPaymentMethod('inStore')}>
                            <Radio value="inStore" size="small" sx={{ color: BRAND.accentText, '&.Mui-checked': { color: BRAND.accentText }, p: 0.5 }} />
                            <Typography variant="body2" fontWeight={selectedPaymentMethod === 'inStore' ? 600 : 400} sx={{ color: BRAND.text }}>{t('customer.appointments.booking.payInStore')}</Typography>
                          </Box>
                        ) : null}
                        {loadingBookingEntitlements ? (
                          <CircularProgress size={16} sx={{ color: BRAND.accentText, alignSelf: 'flex-end', mt: 0.5 }} />
                        ) : null}
                      </Box>
                    </RadioGroup>
                  ) : (
                    <Typography variant="body2" fontWeight={600} sx={{ color: BRAND.text }}>
                      {t(hasUnpricedSelection
                        ? 'customer.appointments.booking.priceToConfirm'
                        : appointmentAllowsInStore
                          ? 'customer.appointments.booking.payInStore'
                          : 'customer.appointments.booking.payOnline')}
                    </Typography>
                  )}
                </Box>
              )}
              </>
              ) : null}
            </Box>
            {payingOnline && !selectedMembershipPlanId ? (
              <Box sx={{ mb: 2 }}>
                <BookingPaymentTerms
                  quote={prepaymentQuote}
                  loading={loadingPrepaymentQuote}
                  failed={prepaymentQuoteFailed}
                  payInFull={payInFull}
                  onPayInFullChange={setPayInFull}
                  accepted={policyAccepted}
                  onAcceptedChange={setPolicyAccepted}
                  currencyCode={currencyCode}
                />
              </Box>
            ) : null}
            {selectedSlotRequiresConfirmation && (
              <Alert severity="info" sx={{ mb: 2, borderRadius: 1.5 }}>
                {t('customer.appointments.booking.requiresConfirmationHint')}
              </Alert>
            )}
            <TextField
              fullWidth
              multiline
              rows={2}
              placeholder={t('customer.appointments.booking.detailsPlaceholder')}
              value={detailsFromCustomer}
              onChange={(e) => setDetailsFromCustomer(e.target.value)}
              size="small"
              sx={{ mb: 2, '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
            />
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Button onClick={() => setActiveStep(STEP_CALENDAR)} sx={{ color: BRAND.accentText, fontWeight: 600 }}>{t('customer.appointments.booking.back')}</Button>
              <Button
                variant="contained"
                onClick={handleConfirm}
                // Paying online is only possible once the terms have been shown and accepted.
                disabled={submitting
                  || releasingCheckout
                  || (payingOnline && !selectedMembershipPlanId
                    && (loadingPrepaymentQuote || !activePaymentOption || (requiresPolicyAcceptance && !policyAccepted)))}
                sx={{ bgcolor: BRAND.accent, color: BRAND.onAccent, px: 3, ...hoverCapableStyles({ bgcolor: BRAND.accentDark }) }}
              >
                {submitting
                  ? <CircularProgress size={22} sx={{ color: BRAND.onAccent }} />
                  : t(payingOnline ? 'customer.appointments.booking.confirmAndPay' : 'customer.appointments.booking.confirm')}
              </Button>
            </Box>
          </Box>
        </Paper>
      )}

      <ScrollableDialog
        open={pickerModalOpen}
        onClose={() => setPickerModalOpen(false)}
        maxWidth="xs"
        title={<Typography variant="h6" sx={{ fontWeight: 600, color: BRAND.text }}>{t('customer.appointments.booking.chooseProfessional')}</Typography>}
        actionsSx={{ justifyContent: 'flex-end', px: 3, pb: 2 }}
        actions={<Button onClick={() => setPickerModalOpen(false)} sx={{ color: BRAND.accentText }}>{t('common.cancel')}</Button>}
      >
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>{t('customer.appointments.booking.multipleAvailable')}</Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {pickerOptions.map((m) => (
              <Button key={m.id} fullWidth variant="outlined" onClick={() => handlePickerConfirm(m.id)} sx={{ justifyContent: 'flex-start', py: 1.5, borderColor: brandAlpha('accent', 0.4), color: BRAND.accentText, ...hoverCapableStyles({ borderColor: BRAND.accent, bgcolor: brandAlpha('accent', 0.06) }) }}>
                {m.name}
              </Button>
            ))}
          </Box>
      </ScrollableDialog>

      <ScrollableDialog
        open={Boolean(incompatibleProfessionalDialog)}
        onClose={() => setIncompatibleProfessionalDialog(null)}
        maxWidth="xs"
        paperSx={{ maxWidth: 420 }}
        title={<Typography variant="h6" sx={{ fontWeight: 600, color: BRAND.text }}>{t('customer.appointments.booking.incompatibleProfessionalTitle', { name: incompatibleProfessionalDialog?.name ?? '' })}</Typography>}
        actionsSx={{ px: 3, pb: 2, flexDirection: 'column', alignItems: 'stretch', gap: 1, '& > :not(:first-of-type)': { marginLeft: 0 } }}
        actions={(
          <>
            <Button
              fullWidth
              variant="contained"
              onClick={() => {
                setIncompatibleProfessionalDialog(null);
                setSpecificTeamMemberId(canDoAll.length === 1 ? canDoAll[0].id : '');
                setProfessionalMode('specific');
                setActiveStep(STEP_PROFESSIONALS);
              }}
              sx={{ bgcolor: BRAND.accent, color: BRAND.onAccent, ...hoverCapableStyles({ bgcolor: BRAND.accentDark }) }}
            >
              {t('customer.appointments.booking.selectAnotherProfessional')}
            </Button>
            <Button
              fullWidth
              variant="outlined"
              onClick={() => {
                setIncompatibleProfessionalDialog(null);
                setProfessionalMode('perService');
                setPerServiceAssignments({ ...suggestedAssignment });
                setActiveStep(STEP_PROFESSIONALS);
              }}
              sx={{ borderColor: BRAND.accent, color: BRAND.accentText, ...hoverCapableStyles({ borderColor: BRAND.accent, bgcolor: brandAlpha('accent', 0.06) }) }}
            >
              {t('customer.appointments.booking.selectDifferentPerService')}
            </Button>
          </>
        )}
      >
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          {t('customer.appointments.booking.incompatibleProfessionalBody', { name: incompatibleProfessionalDialog?.name ?? '' })}
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          {(incompatibleProfessionalDialog?.services ?? []).map((service) => (
            <Typography key={service.id} variant="body2" sx={{ fontWeight: 500, color: BRAND.text, pl: 1, '&::before': { content: '"•"', mr: 1, color: BRAND.accentText } }}>
              {translateServiceName(service.name)}
            </Typography>
          ))}
        </Box>
      </ScrollableDialog>

      <BookingAddProductsDialog
        open={productDialogOpen}
        products={catalogProducts}
        initialLines={productLines}
        currencyCode={currencyCode}
        onSkip={() => setProductDialogOpen(false)}
        onConfirm={(nextLines) => {
          setProductLines(nextLines);
          setProductDialogOpen(false);
        }}
      />

      <ScrollableDialog
        open={renewalWarningOpen}
        onClose={() => setRenewalWarningOpen(false)}
        maxWidth="xs"
        title={t('customer.appointments.booking.renewalRiskTitle')}
        actionsSx={{ px: 3, pb: 2, justifyContent: 'space-between' }}
        actions={(
          <>
            <Button color="inherit" onClick={() => setRenewalWarningOpen(false)}>
              {t('common.cancel')}
            </Button>
            <PrimaryActionButton
              onClick={() => {
                setRenewalWarningOpen(false);
                if (renewalContinueAction === 'confirm') {
                  renewalAcknowledgedRef.current = true;
                  handleConfirm();
                  return;
                }
                goToReviewStep();
              }}
            >
              {t('customer.appointments.booking.continueAnyway')}
            </PrimaryActionButton>
          </>
        )}
      >
        <Typography variant="body2" color="text.secondary">
          {t('customer.appointments.booking.renewalRiskBody', {
            date: renewalPeriodEndDisplay,
          })}
        </Typography>
      </ScrollableDialog>

      </Box>
      {activeStep === STEP_SERVICES && selectedServices.length > 0 && (
        <Portal>
          <Box
            sx={{
              position: 'fixed',
              bottom: 0,
              left: 0,
              right: 0,
              zIndex: 1200,
              bgcolor: 'background.paper',
              borderTop: `1px solid ${brandAlpha('accent', 0.2)}`,
              px: 2,
              py: 1.5,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              boxShadow: '0 -4px 16px rgba(0,0,0,0.08)',
            }}
          >
            <Typography variant="body2" color="text.secondary">
              {t('customer.appointments.booking.servicesSelected', { count: selectedServices.length })}
            </Typography>
            <Button
              variant="contained"
              onClick={goToProfessionalsOrCalendar}
              disabled={hasInvalidSecondarySelection}
              sx={{ bgcolor: BRAND.accent, color: BRAND.onAccent, px: 3, ...hoverCapableStyles({ bgcolor: BRAND.accentDark }) }}
            >
              {t('customer.appointments.booking.next')}
            </Button>
          </Box>
        </Portal>
      )}
      <BookingWaitlistDialog
        open={waitlistOpen}
        onClose={() => setWaitlistOpen(false)}
        storeId={storeId}
        openingHours={storeOverview?.openingHours}
        authorizationHours={authorizationHoursForPlan}
        specialOpeningDays={specialOpeningDays}
        teamMemberWorkingHours={teamMemberWorkingHours}
        selectedDate={selectedDate}
        serviceIds={selectedServiceIds}
        teamMemberIds={professionalMode === 'any' ? [] : teamMemberIdsForApi}
        membershipPlanId={selectedMembershipPlanId}
        onJoined={() => setSuccessMessage(t('customer.appointments.booking.waitlist.success'))}
      />

      {consentDialog}

      <StripeEmbeddedCheckoutDialog
        open={Boolean(checkoutAppointmentId)}
        onClose={handleCheckoutClose}
        subtitle={activePaymentOption?.nonRefundableAmount > 0
          ? t('customer.appointments.booking.policy.checkoutSubtitle', {
            amount: formatCurrency(activePaymentOption.nonRefundableAmount, prepaymentQuote?.currency || currencyCode),
          })
          : undefined}
        createSession={async () => {
          const session = await createAppointmentCheckoutSession(checkoutAppointmentId);
          checkoutSessionRef.current = session;
          return session;
        }}
        onComplete={handleCheckoutComplete}
      />
    </Box>
  );
}
